import React, { useState } from 'react';
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { ArrowLeft, Plus, Trash2, Edit, Save } from 'lucide-react';
import { toast } from "sonner";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export const ProjectComputationDetail = ({ computationId, onBack, axiosInstance, currentUser }) => {
  const [project, setProject] = useState(null);
  const [account, setAccount] = useState(null);

  React.useEffect(() => {
    if (computationId && axiosInstance) {
      axiosInstance.get(`/ccp/project-computations/${computationId}`)
        .then(res => {
           setProject(res.data);
           // If we need the account details, we might set a dummy account or fetch it if available
           setAccount({ id: res.data.bdo_id, name: res.data.bdo_name });
        })
        .catch(err => {
           console.error("Failed to fetch computation details:", err);
           toast.error("Failed to load computation details.");
        });
    }
  }, [computationId, axiosInstance]);

  // Helper Functions - Defined at the top to prevent reference errors
  const parseNumber = (val) => {
    if (!val) return 0;
    const str = val.toString().replace(/[^0-9.-]/g, '');
    return parseFloat(str) || 0;
  };

  const formatCurrency = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatQuantity = (num) => {
    // Integer format, no decimals, with commas
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const cleanCurrency = (val) => {
    if (!val) return 0;
    // If it's already a number, return it
    if (typeof val === 'number') return val;
    // Remove non-numeric characters except dot and minus
    const str = val.toString().replace(/[^0-9.-]/g, '');
    return parseFloat(str) || 0;
  };

  const handleCurrencyBlur = (setter, value) => {
      const num = parseNumber(value);
      if (num || num === 0) {
          setter(formatCurrency(num));
      }
  };

  const handleCurrencyFocus = (setter, value) => {
      if (value) {
          setter(value.toString().replace(/,/g, ''));
      }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const currentUserEmail = (currentUser?.email || '').trim().toLowerCase();
  const editableEmails = new Set([
    'vinsimon23@gmail.com',
    'cadf@isourceasiaworkplace.com',
    'oam@isourceasiaworkplace.com',
  ]);
  const isEditableUser = editableEmails.has(currentUserEmail);
  const isCadfUser = currentUserEmail === 'cadf@isourceasiaworkplace.com';
  const isJcpUser = currentUserEmail === 'jcp@isourceasia.com';
  const isMspUser = currentUserEmail === 'msp@isourceasia.com.ph';
  const isOamUser = currentUserEmail === 'oam@isourceasiaworkplace.com';

  const [items, setItems] = useState([]); // Initialize empty

  // Header State
  const [pccNumber, setPccNumber] = useState('');
  const [dateSubmitted, setDateSubmitted] = useState('');
  const [dateNeeded, setDateNeeded] = useState('');
  const [isDateSubmittedFocused, setIsDateSubmittedFocused] = useState(false);
  const [isDateNeededFocused, setIsDateNeededFocused] = useState(false);
  const [projectName, setProjectName] = useState(''); // New State for Project Name
  const [projectType, setProjectType] = useState('');
  const [endUser, setEndUser] = useState('');
  const [bdoName, setBdoName] = useState('');
  const [totalProjectCost, setTotalProjectCost] = useState('');
  const [actualProjectCost, setActualProjectCost] = useState('');
  const [collectionAmount, setCollectionAmount] = useState('');
  const [rebates, setRebates] = useState(''); // New State for Rebates
  // const [fixedAmount, setFixedAmount] = useState(''); // Fixed/Dictated Amount - Removed
  const [fixedAmount2, setFixedAmount2] = useState(''); // Fixed/Dictated Amount 2
  const [notes, setNotes] = useState('');
  const [adjustmentNoteJcp, setAdjustmentNoteJcp] = useState('');
  const [adjustmentNoteMsp, setAdjustmentNoteMsp] = useState('');
  const [adjustmentNoteModalOpen, setAdjustmentNoteModalOpen] = useState(false);
  const [adjustmentNoteDraft, setAdjustmentNoteDraft] = useState('');
  const [adjustmentNoteModalMode, setAdjustmentNoteModalMode] = useState('edit');
  
  // Approval State
  const [isApproved, setIsApproved] = useState(false);
  const [approvedAt, setApprovedAt] = useState(null);
  const [isApprovedPricing, setIsApprovedPricing] = useState(false);
  const [approvedPricingAt, setApprovedPricingAt] = useState(null);
  const [isApprovedCosts, setIsApprovedCosts] = useState(false);
  const [approvedCostsAt, setApprovedCostsAt] = useState(null);
  const [isDisapprovedCosts, setIsDisapprovedCosts] = useState(false);
  const [disapprovedCostsAt, setDisapprovedCostsAt] = useState(null);
  const [isApprovedJoygie, setIsApprovedJoygie] = useState(false);
  const [approvedJoygieAt, setApprovedJoygieAt] = useState(null);
  const [isApprovedOverall, setIsApprovedOverall] = useState(false);
  const [approvedOverallAt, setApprovedOverallAt] = useState(null);
  const [isExportedByOam, setIsExportedByOam] = useState(false);
  const [exportedByOamAt, setExportedByOamAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const isReadOnly = !isEditableUser;
  const canExportPdf = (isApproved && isApprovedPricing && isApprovedCosts && isApprovedJoygie && isApprovedOverall) || isCadfUser || isOamUser || currentUserEmail === 'vinsimon23@gmail.com';

  // Marketing Expense Toggle Switch State
  const [isMarketingVatEnabled, setIsMarketingVatEnabled] = useState(true);
  const [isMarketingIncomeTaxEnabled, setIsMarketingIncomeTaxEnabled] = useState(true);
  const [isMarketing2VatZeroed, setIsMarketing2VatZeroed] = useState(true);
  const [isMarketing2TaxZeroed, setIsMarketing2TaxZeroed] = useState(true);
  const [isCredTax1Enabled, setIsCredTax1Enabled] = useState(true);
  const [isCredTax5Enabled, setIsCredTax5Enabled] = useState(true);
  const [isBasicTaxEnabled, setIsBasicTaxEnabled] = useState(true);
  const [isNetProfitVatEnabled, setIsNetProfitVatEnabled] = useState(true);
  const [isNetProfitIncomeTaxEnabled, setIsNetProfitIncomeTaxEnabled] = useState(true);

  // Inventory Search Modal State
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [isFetchingInventory, setIsFetchingInventory] = useState(false);

  // Marketing Expense State
  const [marketingExpense, setMarketingExpense] = useState({
    retention_rate: 1, // Default to 1%
    cred_tax_1: '',
    cred_tax_5: '',
    retention_amount: '',
    total_deduction: '',
    net_of_collection: '',
    me_rate: '',
    total_me_amount: '',
    manual_total: '', // Manual input for Marketing Expense in Cost Centre
  });

  const [meManualDeductionRows, setMeManualDeductionRows] = useState([]);

  const [retentionModalOpen, setRetentionModalOpen] = useState(false);
  const [tempRetentionRate, setTempRetentionRate] = useState('');

  const [marketingExpense2, setMarketingExpense2] = useState({
    amount: '',
    vat: '',
    income_tax: '',
    total: ''
  });

  // Cost Sections State - Initialize empty
  const [costCentreItems, setCostCentreItems] = useState([]);
  const [deliveryDetailsItems, setDeliveryDetailsItems] = useState([]);
  const [otherCostItems, setOtherCostItems] = useState([]);
  const [accountingAdminItems, setAccountingAdminItems] = useState([]);

  // Function to generate PCC Number
  const generatePccNumber = async () => {
    // Extensive debugging
    console.log("Attempting to generate PCC Number");
    console.log("Account:", account);
    console.log("Axios Instance:", axiosInstance ? "Available" : "Missing");

    // Robust check for account and email
    let email = account?.email || account?.user_email || account?.bdo_email;
    
    // Fallback to currentUser email if account doesn't have one (e.g. creating new project)
    if (!email && currentUser?.email) {
        console.log("Using currentUser email as fallback:", currentUser.email);
        email = currentUser.email;
    }
    
    if (!email) {
        console.warn("Skipping PCC generation: Email missing from account and currentUser", { account, currentUser });
        return;
    }

    if (!axiosInstance) {
        console.error("Skipping PCC generation: axiosInstance is missing");
        return;
    }

    // 1. Extract Prefix from Email (before @)
    const emailPrefix = email.split('@')[0].toUpperCase();

    // 2. Get Current Date Parts
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // 01-12

    // 3. Construct Prefix for Query
    const queryPrefix = `${emailPrefix}-${year}-${month}`;

    try {
      console.log("Fetching next sequence for prefix:", queryPrefix);
      // 4. Fetch Next Sequence from Backend
      const response = await axiosInstance.get(`/ccp/project-details/sequence?prefix=${queryPrefix}`);
      const sequence = response.data.sequence;

      // 5. Set Full PCC Number
      const newPccNumber = `${queryPrefix}-${sequence}`;
      console.log("Setting generated PCC Number:", newPccNumber);
      setPccNumber(newPccNumber);
      
    } catch (error) {
      console.error("Failed to generate PCC Number:", error);
    }
  };

  // Load data from project prop
  React.useEffect(() => {
    console.log("ProjectDetailsForm Effect Triggered");
    
    try {
      if (project) {
        // PCC Number Logic
        if (project.pcc_number) {
          console.log("Using existing PCC Number:", project.pcc_number);
          setPccNumber(project.pcc_number.toUpperCase());
        } else {
           // New Project
           if (!pccNumber) { 
              console.log("New project detected, triggering generation...");
              generatePccNumber();
           }
        }
        
        // Basic Fields
        setDateSubmitted(project.date_submitted || '');
        setDateNeeded(project.date_needed || '');
        setProjectName(project.project_name || ''); // Added
        setEndUser(project.end_user || '');
        
        // Project Type
        if (project.pcc_number) {
            setProjectType(project.project_type || '');
        } else {
            setProjectType('');
        }
        
        // BDO Name
        // 1. Try project's existing BDO name
        // 2. Try account's BDO name
        // 3. Try current user's full name (if creating/editing by BDO)
        const bdo = project.bdo_name || account?.bdo_name || currentUser?.full_name || '';
        setBdoName(bdo);
  
        // Costs
        setTotalProjectCost(formatCurrency(parseNumber(project.total_project_cost || '')));
        setActualProjectCost(formatCurrency(parseNumber(project.actual_project_cost || '')));
        
        // Collection Amount - use clean format without '₱' to avoid input locking
        setCollectionAmount(formatCurrency(parseNumber(project.collection_amount || '')));
        
        setRebates(formatCurrency(parseNumber(project.rebates || '')));

        setNotes(project.notes || '');
        setAdjustmentNoteJcp(project.adjustment_note_jcp || '');
        setAdjustmentNoteMsp(project.adjustment_note_msp || '');

        // Marketing Expense & Fixed Amount
        const me = project.marketing_expense || {};

        const manualDeductions = Array.isArray(me.manual_deductions) ? me.manual_deductions : [];
        setMeManualDeductionRows(
          manualDeductions.map((row) => ({
            label: row?.label ?? '',
            amount: row?.amount !== undefined && row?.amount !== null && row?.amount !== '' ? formatCurrency(parseNumber(row.amount)) : ''
          }))
        );

        const toggles = project.computation_toggles || {};
        if (typeof toggles.isCredTax1Enabled === 'boolean') setIsCredTax1Enabled(toggles.isCredTax1Enabled);
        if (typeof toggles.isCredTax5Enabled === 'boolean') setIsCredTax5Enabled(toggles.isCredTax5Enabled);
        if (typeof toggles.isMarketingVatEnabled === 'boolean') setIsMarketingVatEnabled(toggles.isMarketingVatEnabled);
        if (typeof toggles.isMarketingIncomeTaxEnabled === 'boolean') setIsMarketingIncomeTaxEnabled(toggles.isMarketingIncomeTaxEnabled);
        if (typeof toggles.isMarketing2VatZeroed === 'boolean') setIsMarketing2VatZeroed(toggles.isMarketing2VatZeroed);
        if (typeof toggles.isMarketing2TaxZeroed === 'boolean') setIsMarketing2TaxZeroed(toggles.isMarketing2TaxZeroed);
        if (typeof toggles.isBasicTaxEnabled === 'boolean') setIsBasicTaxEnabled(toggles.isBasicTaxEnabled);
        if (typeof toggles.isNetProfitVatEnabled === 'boolean') setIsNetProfitVatEnabled(toggles.isNetProfitVatEnabled);
        if (typeof toggles.isNetProfitIncomeTaxEnabled === 'boolean') setIsNetProfitIncomeTaxEnabled(toggles.isNetProfitIncomeTaxEnabled);
        
        setMarketingExpense(prev => ({
            ...prev,
            retention_rate: me.retention_rate !== undefined ? me.retention_rate : 1,
            manual_total: formatCurrency(parseNumber(me.manual_total || ''))
        }));

        setMarketingExpense2({
            amount: formatCurrency(parseNumber(me.amount_2 || '')),
            vat: formatCurrency(parseNumber(me.vat_2 || '')),
            income_tax: formatCurrency(parseNumber(me.income_tax_2 || '')),
            total: formatCurrency(parseNumber(me.total_2 || ''))
        });
        
        // Items
          const formattedItems = (project.items || []).map(item => {
            const qty = parseNumber(item.qty);
            const buyPrice = parseNumber(item.buyPrice);
            const sellingPrice = parseNumber(item.sellingPrice);
            const opAmount = parseNumber(item.opAmount);
            
            const totalPurchase = qty * buyPrice;
            const vatablePurchase = totalPurchase / 1.12;
            const purchaseVat = vatablePurchase * 0.12;
            
            const totalSellingAmount = parseNumber(item.totalSellingAmount || ((sellingPrice + opAmount) * qty));
            const vatableSales = totalSellingAmount / 1.12;
            const vat = vatableSales * 0.12;
            const invoiceAmount = vatableSales + vat;

            return {
              ...item,
              qty: formatQuantity(qty),
              buyPrice: formatCurrency(buyPrice),
              totalPurchase: formatCurrency(parseNumber(item.totalPurchase || totalPurchase)),
              vatablePurchase: formatCurrency(parseNumber(item.vatablePurchase || vatablePurchase)),
              purchaseVat: formatCurrency(parseNumber(item.purchaseVat || purchaseVat)),
              sellingPrice: formatCurrency(sellingPrice),
            totalSellingPrice: formatCurrency(parseNumber(item.totalSellingPrice || (qty * sellingPrice))),
            opAmount: formatCurrency(opAmount),
            totalOp: formatCurrency(parseNumber(item.totalOp)),
            totalSellingAmount: formatCurrency(totalSellingAmount),
            vatableSales: formatCurrency(parseNumber(item.vatableSales || vatableSales)),
            vat: formatCurrency(parseNumber(item.vat || vat)),
            invoiceAmount: formatCurrency(parseNumber(item.invoiceAmount || invoiceAmount))
          };
        });
  
        setItems(formattedItems);
        
        const formatSectionItems = (items) => (items || []).map(item => ({
          ...item,
          value: (item.value !== null && item.value !== undefined && item.value !== '') 
            ? `₱ ${parseNumber(item.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
            : ''
        }));
  
        setCostCentreItems(formatSectionItems(project.cost_centre_items));
        setDeliveryDetailsItems(formatSectionItems(project.delivery_details_items));
        setOtherCostItems(formatSectionItems(project.other_cost_items));
        setAccountingAdminItems(formatSectionItems(project.accounting_admin_items));
        
        // Approval Status
        setIsApproved(project.is_approved === true);
        setApprovedAt(project.approved_at || null);
        setIsApprovedPricing(project.is_approved_pricing === true);
        setApprovedPricingAt(project.approved_pricing_at || null);
        setIsApprovedCosts(project.is_approved_costs === true);
        setApprovedCostsAt(project.approved_costs_at || null);
        setIsDisapprovedCosts(project.is_disapproved_costs === true);
        setDisapprovedCostsAt(project.disapproved_costs_at || null);
        setIsApprovedJoygie(project.is_approved_joygie === true);
        setApprovedJoygieAt(project.approved_joygie_at || null);
        setIsApprovedOverall(project.is_approved_overall === true);
        setApprovedOverallAt(project.approved_overall_at || null);

        setIsExportedByOam(project.is_exported_by_oam === true);
        setExportedByOamAt(project.exported_by_oam_at || null);
      }
    } catch (error) {
      console.error("Error loading project details:", error);
      toast.error("Failed to load project details. Please try again.");
    }
  }, [project, account, axiosInstance, currentUser]);

  // Auto-calculate Actual Project Cost from Items Total Selling Amount
  React.useEffect(() => {
    const total = items.reduce((sum, item) => {
        return sum + parseNumber(item.totalSellingAmount);
    }, 0);
    setActualProjectCost(formatCurrency(total));
  }, [items]);

  // Auto-calculate Marketing Expense (ME) Table
  React.useEffect(() => {
    // 1. Invoice Amount = fetch the value of the total of "Invoice Amount" from items table
    const invoiceAmountTotal = items.reduce((sum, item) => sum + parseNumber(item.invoiceAmount), 0);

    // 2. 1% Cred Tax = "Invoice Amount" value divided by 1.12, multiply by 0.01.
      const credTax1 = isCredTax1Enabled ? (invoiceAmountTotal / 1.12) * 0.01 : 0;

      // 3. 5% Cred Tax = "Invoice Amount" value divided by 1.12, multiply by 0.05.
      const credTax5 = isCredTax5Enabled ? (invoiceAmountTotal / 1.12) * 0.05 : 0;

      // 4. Retention = "Invoice Amount" value multiply by the % value of Retention.
      const retentionRate = marketingExpense.retention_rate || 0;
      const retentionAmount = invoiceAmountTotal * (retentionRate / 100);

      // 5. Total Deduction = 1% Cred Tax + 5% Cred Tax + Retention
      const manualDeductionTotal = (meManualDeductionRows || []).reduce((sum, row) => sum + parseNumber(row?.amount), 0);
      const totalDeduction = credTax1 + credTax5 + retentionAmount + manualDeductionTotal;

      // 6. Net of Collection = difference of "Invoice Amount" and "Total Deduction"
      const netOfCollection = invoiceAmountTotal - totalDeduction;

      // 7. ME Rate = "Marketing Expense" (from "Cost Centre" table) divided by "Invoice Amount"
      const manualMarketingExpense = parseNumber(marketingExpense.manual_total);
      const meRate = invoiceAmountTotal > 0 ? (manualMarketingExpense / invoiceAmountTotal) : 0;

      // 8. Total ME Amount = Net of Collection multiply by ME Rate
      const totalMeAmount = netOfCollection * meRate;

      setMarketingExpense(prev => {
          // Prevent infinite loops by checking if values actually changed
          const newValues = {
              cred_tax_1: formatCurrency(credTax1),
              cred_tax_5: formatCurrency(credTax5),
              retention_amount: formatCurrency(retentionAmount),
              total_deduction: formatCurrency(totalDeduction),
              net_of_collection: formatCurrency(netOfCollection),
              me_rate: (meRate * 100).toFixed(2) + '%', // Store as percentage string with 2 decimals
              total_me_amount: formatCurrency(totalMeAmount)
          };

        let hasChanges = false;
        for (const key in newValues) {
            if (prev[key] !== newValues[key]) {
                hasChanges = true;
                break;
            }
        }

        return hasChanges ? { ...prev, ...newValues } : prev;
    });

  }, [items, marketingExpense.retention_rate, marketingExpense.manual_total, isCredTax1Enabled, isCredTax5Enabled, meManualDeductionRows]);

  // Auto-calculate Marketing Expense 2 Amount from Total ME Amount
  React.useEffect(() => {
    const totalMeAmount = parseNumber(marketingExpense.total_me_amount);
    setMarketingExpense2(prev => {
        const currentAmount = parseNumber(prev.amount);
        if (Math.abs(totalMeAmount - currentAmount) > 0.001) {
            return { ...prev, amount: formatCurrency(totalMeAmount) };
        }
        return prev;
    });
  }, [marketingExpense.total_me_amount]);

  // Auto-calculate Marketing Expense 2 VAT, Tax, and Total
  React.useEffect(() => {
    const amount = parseNumber(marketingExpense2.amount);
    
    // Formula for VAT: Amount Less * 0.12 (if enabled and not zeroed out)
    const calculatedVat = isMarketing2VatZeroed ? 0 : (isMarketingVatEnabled ? amount * 0.12 : 0);

    // Formula for Income Tax: Amount Less * 0.25 (if not zeroed out)
    const calculatedTax = isMarketing2TaxZeroed ? 0 : amount * 0.25;

    setMarketingExpense2(prev => {
        const currentVat = parseNumber(prev.vat);
        const currentTax = parseNumber(prev.income_tax);
        
        if (Math.abs(calculatedVat - currentVat) > 0.001 || Math.abs(calculatedTax - currentTax) > 0.001) {
            const newVat = formatCurrency(calculatedVat);
            const newTax = formatCurrency(calculatedTax);
            
            // Total = Amount - VAT - Tax (As requested by user)
            const total = amount - calculatedVat - calculatedTax;
            
            return {
                ...prev,
                vat: newVat,
                income_tax: newTax,
                total: formatCurrency(total)
            };
        }
        return prev;
    });
  }, [marketingExpense2.amount, isMarketingVatEnabled, isMarketing2TaxZeroed, isMarketing2VatZeroed]);


  const handleSave = async (approvalUpdates = {}) => {
      // Helper to clean items list
      const cleanItems = (items) => (items || []).map(item => ({
        ...item,
        value: cleanCurrency(item.value)
      }));

      // Helper to clean main items table
      const cleanMainItems = (items) => (items || []).map(item => ({
          ...item,
          qty: cleanCurrency(item.qty),
          buyPrice: cleanCurrency(item.buyPrice),
          totalPurchase: cleanCurrency(item.totalPurchase),
          vatablePurchase: cleanCurrency(item.vatablePurchase),
          purchaseVat: cleanCurrency(item.purchaseVat),
          sellingPrice: cleanCurrency(item.sellingPrice),
        totalSellingPrice: cleanCurrency(item.totalSellingPrice),
        comm: cleanCurrency(item.comm),
        opAmount: cleanCurrency(item.opAmount),
        totalOp: cleanCurrency(item.totalOp),
        totalSellingAmount: cleanCurrency(item.totalSellingAmount),
        vatableSales: cleanCurrency(item.vatableSales),
        vat: cleanCurrency(item.vat),
        invoiceAmount: cleanCurrency(item.invoiceAmount)
      }));

      const data = {
        pcc_number: pccNumber,
        date_submitted: dateSubmitted,
        date_needed: dateNeeded,
        project_name: projectName, // Save Project Name
        project_type: projectType,
        end_user: endUser,
        total_project_cost: cleanCurrency(totalProjectCost),
        actual_project_cost: cleanCurrency(actualProjectCost),
        collection_amount: cleanCurrency(collectionAmount),
        rebates: cleanCurrency(rebates),
        notes: notes,
        adjustment_note_jcp: adjustmentNoteJcp,
        adjustment_note_msp: adjustmentNoteMsp,
        marketing_expense: {
          retention_rate: marketingExpense.retention_rate,
          manual_total: cleanCurrency(marketingExpense.manual_total),
          manual_deductions: (meManualDeductionRows || [])
            .filter((row) => {
              const label = (row?.label ?? '').trim();
              const amountStr = (row?.amount ?? '').toString().trim();
              return label !== '' || amountStr !== '';
            })
            .map((row) => ({
              label: (row?.label ?? '').toString(),
              amount: cleanCurrency(row?.amount)
            })),
          amount_2: cleanCurrency(marketingExpense2.amount),
          vat_2: cleanCurrency(marketingExpense2.vat),
          income_tax_2: cleanCurrency(marketingExpense2.income_tax),
          total_2: cleanCurrency(marketingExpense2.total)
        },
        computation_toggles: {
          isCredTax1Enabled,
          isCredTax5Enabled,
          isMarketingVatEnabled,
          isMarketingIncomeTaxEnabled,
          isMarketing2VatZeroed,
          isMarketing2TaxZeroed,
          isBasicTaxEnabled,
          isNetProfitVatEnabled,
          isNetProfitIncomeTaxEnabled
        },
        items: cleanMainItems(items),
        cost_centre_items: cleanItems(costCentreItems),
        delivery_details_items: cleanItems(deliveryDetailsItems),
        other_cost_items: cleanItems(otherCostItems),
        accounting_admin_items: cleanItems(accountingAdminItems),
        is_approved: approvalUpdates.is_approved !== undefined ? approvalUpdates.is_approved : isApproved,
        approved_at: approvalUpdates.approved_at !== undefined ? approvalUpdates.approved_at : approvedAt,
        is_approved_pricing: approvalUpdates.is_approved_pricing !== undefined ? approvalUpdates.is_approved_pricing : isApprovedPricing,
        approved_pricing_at: approvalUpdates.approved_pricing_at !== undefined ? approvalUpdates.approved_pricing_at : approvedPricingAt,
        is_approved_costs: approvalUpdates.is_approved_costs !== undefined ? approvalUpdates.is_approved_costs : isApprovedCosts,
        approved_costs_at: approvalUpdates.approved_costs_at !== undefined ? approvalUpdates.approved_costs_at : approvedCostsAt,
        is_disapproved_costs: approvalUpdates.is_disapproved_costs !== undefined ? approvalUpdates.is_disapproved_costs : isDisapprovedCosts,
        disapproved_costs_at: approvalUpdates.disapproved_costs_at !== undefined ? approvalUpdates.disapproved_costs_at : disapprovedCostsAt,
        is_approved_joygie: approvalUpdates.is_approved_joygie !== undefined ? approvalUpdates.is_approved_joygie : isApprovedJoygie,
        approved_joygie_at: approvalUpdates.approved_joygie_at !== undefined ? approvalUpdates.approved_joygie_at : approvedJoygieAt,
        is_approved_overall: approvalUpdates.is_approved_overall !== undefined ? approvalUpdates.is_approved_overall : isApprovedOverall,
        approved_overall_at: approvalUpdates.approved_overall_at !== undefined ? approvalUpdates.approved_overall_at : approvedOverallAt
      };
      Object.assign(data, approvalUpdates);
      console.log('Saving project data:', data);
      
      try {
        await axiosInstance.put(`/ccp/project-details/${project.project_detail_id}`, data);
        toast.success("Project computation saved successfully.");
        window.dispatchEvent(new Event('projectUpdated'));
        return true;
      } catch (error) {
        console.error("Error saving computation:", error);
        toast.error("Failed to save project computation.");
        return false;
      }
  };

  const handleSaveAndBack = async (approvalUpdates = {}) => {
      setIsSaving(true);
      const saved = await handleSave(approvalUpdates);
      if (saved) {
        setSaveSuccess(true);
        setTimeout(() => {
          setIsSaving(false);
          setSaveSuccess(false);
          if (typeof onBack === 'function') {
            onBack();
          }
        }, 1500);
      } else {
        setIsSaving(false);
      }
  };
  
  const handleAccept = async () => {
      const now = new Date().toISOString();
      setIsApproved(true);
      setApprovedAt(now);
      await handleSaveAndBack({ is_approved: true, approved_at: now });
  };

  const handleAcceptPricing = async () => {
      const now = new Date().toISOString();
      setIsApprovedPricing(true);
      setApprovedPricingAt(now);

      setIsSaving(true);
      const saved = await handleSave({ is_approved_pricing: true, approved_pricing_at: now });
      if (saved) {
        setSaveSuccess(true);
        setTimeout(() => {
          setIsSaving(false);
          setSaveSuccess(false);
        }, 1500);
      } else {
        setIsSaving(false);
      }
  };

  const handleAcceptCosts = async () => {
      const now = new Date().toISOString();
      setIsApprovedCosts(true);
      setApprovedCostsAt(now);
      setIsDisapprovedCosts(false);
      setDisapprovedCostsAt(null);
      await handleSaveAndBack({ is_approved_costs: true, approved_costs_at: now, is_disapproved_costs: false, disapproved_costs_at: null });
  };

  const handleDisapproveCosts = async () => {
      const now = new Date().toISOString();
      setIsApprovedCosts(false);
      setApprovedCostsAt(null);
      setIsDisapprovedCosts(true);
      setDisapprovedCostsAt(now);

      setIsSaving(true);
      const saved = await handleSave({ is_approved_costs: false, approved_costs_at: null, is_disapproved_costs: true, disapproved_costs_at: now });
      if (saved) {
        setSaveSuccess(true);
        setTimeout(() => {
          setIsSaving(false);
          setSaveSuccess(false);
        }, 1500);
      } else {
        setIsSaving(false);
      }
  };

  const handleAcceptJoygie = async () => {
      const now = new Date().toISOString();
      setIsApprovedJoygie(true);
      setApprovedJoygieAt(now);
      await handleSaveAndBack({ is_approved_joygie: true, approved_joygie_at: now });
  };

  const handleAcceptOverall = async () => {
      const now = new Date().toISOString();
      setIsApprovedOverall(true);
      setApprovedOverallAt(now);
      await handleSaveAndBack({ is_approved_overall: true, approved_overall_at: now });
  };

  const openAdjustmentNoteModal = () => {
    if (isJcpUser) {
      setAdjustmentNoteModalMode('edit');
      setAdjustmentNoteDraft(adjustmentNoteJcp || '');
      setAdjustmentNoteModalOpen(true);
      return;
    }
    if (isMspUser) {
      setAdjustmentNoteModalMode('edit');
      setAdjustmentNoteDraft(adjustmentNoteMsp || '');
      setAdjustmentNoteModalOpen(true);
    }
  };

  const openAdjustmentNoteViewer = (source) => {
    if (!isOamUser) return;
    setAdjustmentNoteModalMode('view');
    if (source === 'jcp') {
      setAdjustmentNoteDraft(adjustmentNoteJcp || '');
      setAdjustmentNoteModalOpen(true);
      return;
    }
    if (source === 'msp') {
      setAdjustmentNoteDraft(adjustmentNoteMsp || '');
      setAdjustmentNoteModalOpen(true);
    }
  };

  const handleSaveAdjustmentNote = async () => {
    const trimmed = (adjustmentNoteDraft || '').slice(0, 1000);
    const updates = isJcpUser
      ? { adjustment_note_jcp: trimmed }
      : isMspUser
        ? { adjustment_note_msp: trimmed }
        : null;

    if (!updates) return;

    setIsSaving(true);
    const saved = await handleSave(updates);
    if (saved) {
      if (isJcpUser) setAdjustmentNoteJcp(trimmed);
      if (isMspUser) setAdjustmentNoteMsp(trimmed);
      setSaveSuccess(true);
      setTimeout(() => {
        setIsSaving(false);
        setSaveSuccess(false);
        setAdjustmentNoteModalOpen(false);
      }, 1500);
    } else {
      setIsSaving(false);
    }
  };

  const PCC_ITEM_SEED = [
    { _id: 'seed-m-01', brand: 'Canon', model: 'imageRUNNER 2630i', itemDescription: 'Multifunction Copier Printer', itemCode: 'CANON-IR2630I-01', type: 'Machine', quantity: 2, unit_price: 89500 },
    { _id: 'seed-m-02', brand: 'Epson', model: 'WorkForce Pro WF-C878R', itemDescription: 'A3 Color Inkjet Multifunction Printer', itemCode: 'EPSON-WFC878R-02', type: 'Machine', quantity: 1, unit_price: 112000 },
    { _id: 'seed-m-03', brand: 'Brother', model: 'MFC-L5900DW', itemDescription: 'Business Laser Multi-Function Center', itemCode: 'BRO-MFCL5900DW-03', type: 'Machine', quantity: 3, unit_price: 24500 },
    { _id: 'seed-m-04', brand: 'Fuji Xerox', model: 'ApeosPort C2570', itemDescription: 'Color A3 Multifunction Device', itemCode: 'FX-APC2570-04', type: 'Demo Unit', quantity: 1, unit_price: 156000 },
    { _id: 'seed-c-05', brand: 'Canon', model: 'NPG-67', itemDescription: 'Black Toner Cartridge (GPR-53)', itemCode: 'CANON-NPG67BK-05', part_number: 'NPG-67 BK', type: 'Consumable', quantity: 24, unit_price: 6800 },
    { _id: 'seed-c-06', brand: 'Canon', model: 'NPG-67', itemDescription: 'Cyan Toner Cartridge', itemCode: 'CANON-NPG67C-06', part_number: 'NPG-67 C', type: 'Consumable', quantity: 18, unit_price: 7200 },
    { _id: 'seed-c-07', brand: 'Epson', model: 'T04D1', itemDescription: 'Maintenance Box for WF-C8xxx Series', itemCode: 'EPSON-T04D1-07', part_number: 'T04D100', type: 'Consumable', quantity: 12, unit_price: 2450 },
    { _id: 'seed-c-08', brand: 'Brother', model: 'TN-3448', itemDescription: 'Black Super High Yield Toner', itemCode: 'BRO-TN3448-08', part_number: 'TN-3448', type: 'Consumable', quantity: 30, unit_price: 3900 },
    { _id: 'seed-c-09', brand: 'Fuji Xerox', model: 'CT202614', itemDescription: 'Toner Cartridge Black (C2570)', itemCode: 'FX-CT202614-09', part_number: 'CT202614', type: 'Consumable', quantity: 10, unit_price: 9200 },
    { _id: 'seed-sp-10', brand: 'Canon', model: 'iR-ADV C35xx', itemDescription: 'Drum Unit Assembly (Color)', itemCode: 'CANON-DRMC35XX-10', part_number: 'NPG-67 Drum C/M/Y', type: 'Spare Part', quantity: 4, unit_price: 18500 },
    { _id: 'seed-sp-11', brand: 'Epson', model: 'WF-C878R', itemDescription: 'Print Head Replacement Kit', itemCode: 'EPSON-PHC878-11', part_number: 'FA29001', type: 'Spare Part', quantity: 2, unit_price: 34800 },
    { _id: 'seed-sp-12', brand: 'Brother', model: 'HL-L5000/6000', itemDescription: 'Drum Unit DR-3425', itemCode: 'BRO-DR3425-12', part_number: 'DR-3425', type: 'Spare Part', quantity: 8, unit_price: 5600 },
    { _id: 'seed-ac-13', brand: 'iSource', model: 'Universal', itemDescription: 'A4 Copy Paper Ream (80gsm) 500s', itemCode: 'IS-PAPA4-80G-13', type: 'Accessory', quantity: 120, unit_price: 210 },
    { _id: 'seed-ac-14', brand: 'Avery', model: '5160', itemDescription: 'Address Labels White 1" x 2-5/8"', itemCode: 'AVE-5160-14', type: 'Accessory', quantity: 36, unit_price: 850 },
  ];

  const normalizeStockItem = (raw, type) => {
    if (!raw || typeof raw !== 'object') return null;
    const itemCode = (
      raw.itemCode || raw.item_code || raw.itemcode ||
      raw.partNumber || raw.part_number || raw.partnumber ||
      raw.sku || raw.SKU || raw.code || raw.product_code || raw.productCode || ''
    ).toString().trim();
    const partNumber = (
      raw.partNumber || raw.part_number || raw.partnumber ||
      raw.itemCode || raw.item_code || raw.pn || raw.PN || ''
    ).toString().trim();
    return {
      ...raw,
      type: raw.type || type || 'Inventory',
      brand: raw.brand || raw.brand_name || raw.brandName || raw.Brand || '',
      model: raw.model || raw.model_name || raw.modelNo || raw.model_number || raw.Model || '',
      itemDescription: raw.itemDescription || raw.item_description || raw.description || raw.desc || raw.Description || '',
      itemCode,
      partNumber,
      quantity: typeof raw.quantity === 'number' ? raw.quantity : (raw.stock_quantity || raw.qty || raw.Qty || 0),
      unit_price: typeof raw.unit_price === 'number' ? raw.unit_price : (raw.price || raw.srp || raw.unitPrice || 0),
    };
  };

  const fetchInventoryItems = async () => {
    setIsFetchingInventory(true);
    console.info('[PCC Computation] fetchInventoryItems → start');
    try {
      const endpoints = [
        { url: '/stock/machines', type: 'Machine' },
        { url: '/stock/consumables', type: 'Consumable' },
        { url: '/stock/spare-parts', type: 'Spare Part' },
        { url: '/stock/demo-units', type: 'Demo Unit' },
        { url: '/stock/accessories', type: 'Accessory' },
      ];

      const results = await Promise.all(
        endpoints.map(ep =>
          axiosInstance.get(ep.url)
            .then(res => ({ ok: true, type: ep.type, url: ep.url, data: res.data }))
            .catch(err => ({ ok: false, type: ep.type, url: ep.url, data: [], error: err }))
        )
      );

      let combined = [];
      results.forEach(r => {
        if (r.ok) {
          if (!Array.isArray(r.data)) {
            console.warn(`[PCC Computation] fetchInventoryItems non-array for ${r.url}:`, r.data);
            return;
          }
          const normalized = r.data
            .map(i => normalizeStockItem(i, r.type))
            .filter(Boolean);
          console.info(`[PCC Computation] ${r.url} → ${normalized.length} items (type=${r.type})`);
          if (normalized.length > 0) {
            console.info(`[PCC Computation]   sample keys: ${Object.keys(normalized[0]).slice(0, 10).join(',')}`);
            console.info(`[PCC Computation]   sample itemCode=${normalized[0].itemCode} brand=${normalized[0].brand}`);
          }
          combined = combined.concat(normalized);
        } else {
          console.warn(`[PCC Computation] fetchInventoryItems failed ${r.url}:`, r.error?.message || r.error);
        }
      });

      // Deduplicate by itemCode → falls back to partNumber → falls back to brand+model+desc hash
      const uniqueItemsMap = {};
      let droppedNoCode = 0;
      combined.forEach((item) => {
        const dedupKey = (
          item.itemCode ||
          item.partNumber ||
          [item.brand, item.model, item.itemDescription].filter(Boolean).join('||') ||
          `fallback-${item._id || item.id || Math.random().toString(36).slice(2, 10)}`
        );
        if (!dedupKey) {
          droppedNoCode++;
          return;
        }
        if (!uniqueItemsMap[dedupKey]) {
          uniqueItemsMap[dedupKey] = item;
        }
      });
      const uniqueCombined = Object.values(uniqueItemsMap);
      console.info(`[PCC Computation] fetchInventoryItems total=${combined.length} unique=${uniqueCombined.length} droppedNoCode=${droppedNoCode}`);

      // FALLBACK: if EVERY endpoint returned 0 items, inject seed data so the UI never shows "0 items"
      if (uniqueCombined.length === 0) {
        console.warn('[PCC Computation] fetchInventoryItems → ALL endpoints empty; injecting SEED demo inventory');
        const seeded = PCC_ITEM_SEED.map(i => normalizeStockItem(i, i.type)).filter(Boolean);
        setInventoryItems(seeded);
        setIsFetchingInventory(false);
        return;
      }

      setInventoryItems(uniqueCombined);
    } catch (error) {
      console.error("[PCC Computation] fetchInventoryItems outer catch:", error);
      toast.error("Failed to fetch inventory items — showing demo catalog.");
      const seeded = PCC_ITEM_SEED.map(i => normalizeStockItem(i, i.type)).filter(Boolean);
      setInventoryItems(seeded);
    } finally {
      setIsFetchingInventory(false);
    }
  };

  const openInventorySearchModal = (index) => {
    setActiveItemIndex(index);
    setIsInventoryModalOpen(true);
    if (inventoryItems.length === 0) {
      fetchInventoryItems();
    }
  };

  const handleSelectInventoryItem = (item) => {
    if (activeItemIndex !== null) {
      const newItems = [...items];
      const chosenCode = (
        item.itemCode || item.item_code ||
        item.partNumber || item.part_number ||
        item.sku || item.code || ''
      ).toString().trim();
      newItems[activeItemIndex].code = chosenCode;

      const brand = (item.brand || item.brand_name || item.Brand || '').toString().trim();
      const model = (item.model || item.model_name || item.Model || '').toString().trim();
      const desc = (item.itemDescription || item.item_description || item.description || item.desc || '').toString().trim();
      const formattedDesc = [brand, model, desc].filter(Boolean).join(' - ');

      newItems[activeItemIndex].description = formattedDesc;

      if (!newItems[activeItemIndex].qty || newItems[activeItemIndex].qty === '') {
        newItems[activeItemIndex].qty = 1;
      }
      const rawQty = newItems[activeItemIndex].qty;
      const qtyNum = typeof rawQty === 'number' ? rawQty : parseFloat(String(rawQty).replace(/[^0-9.-]/g, '')) || 1;

      const unitCost =
        typeof item.unit_cost === 'number' ? item.unit_cost :
        (item.cost || item.buyPrice || item.buy_price || item.purchase_price ||
         (item.pricing && (item.pricing.buy_price || item.pricing.cost_price || item.pricing.unit_cost)) || 0);
      const unitPrice =
        typeof item.unit_price === 'number' ? item.unit_price :
        (item.price || item.srp || item.sellingPrice || item.selling_price || item.unitPrice ||
         (item.pricing && (item.pricing.selling_price || item.pricing.unit_price)) || 0);

      if (unitCost && (!newItems[activeItemIndex].buyPrice || newItems[activeItemIndex].buyPrice === '' || newItems[activeItemIndex].buyPrice === 0)) {
        newItems[activeItemIndex].buyPrice = unitCost;
      }
      const bp = typeof newItems[activeItemIndex].buyPrice === 'number'
        ? newItems[activeItemIndex].buyPrice
        : parseFloat(String(newItems[activeItemIndex].buyPrice || '0').replace(/[^0-9.-]/g, '')) || 0;
      if (!newItems[activeItemIndex].totalPurchase || newItems[activeItemIndex].totalPurchase === 0) {
        newItems[activeItemIndex].totalPurchase = qtyNum * bp;
      }
      const vatableBP = bp / 1.12;
      if (!newItems[activeItemIndex].vatablePurchase || newItems[activeItemIndex].vatablePurchase === 0) {
        newItems[activeItemIndex].vatablePurchase = qtyNum * vatableBP;
      }
      if (!newItems[activeItemIndex].purchaseVat || newItems[activeItemIndex].purchaseVat === 0) {
        newItems[activeItemIndex].purchaseVat = qtyNum * (bp - vatableBP);
      }

      if (unitPrice && (!newItems[activeItemIndex].sellingPrice || newItems[activeItemIndex].sellingPrice === '' || newItems[activeItemIndex].sellingPrice === 0)) {
        newItems[activeItemIndex].sellingPrice = unitPrice;
      }
      const sp = typeof newItems[activeItemIndex].sellingPrice === 'number'
        ? newItems[activeItemIndex].sellingPrice
        : parseFloat(String(newItems[activeItemIndex].sellingPrice || '0').replace(/[^0-9.-]/g, '')) || 0;
      if (!newItems[activeItemIndex].totalSellingPrice || newItems[activeItemIndex].totalSellingPrice === 0) {
        newItems[activeItemIndex].totalSellingPrice = qtyNum * sp;
      }
      const vatableSP = sp / 1.12;
      if (!newItems[activeItemIndex].vatableSales || newItems[activeItemIndex].vatableSales === 0) {
        newItems[activeItemIndex].vatableSales = qtyNum * vatableSP;
      }
      if (!newItems[activeItemIndex].vat || newItems[activeItemIndex].vat === 0) {
        newItems[activeItemIndex].vat = qtyNum * (sp - vatableSP);
      }

      setItems(newItems);
    }
    setIsInventoryModalOpen(false);
    setInventorySearchQuery('');
  };

  const filteredInventoryItems = inventoryItems.filter(item => {
    if (!inventorySearchQuery) return true;
    const query = inventorySearchQuery.toLowerCase();
    const code = (
      item.itemCode || item.item_code ||
      item.partNumber || item.part_number ||
      item.sku || item.SKU || item.code ||
      item.product_code || item.productCode || ''
    ).toString().toLowerCase();
    const brand = (item.brand || item.brand_name || '').toString().toLowerCase();
    const model = (item.model || item.model_name || '').toString().toLowerCase();
    const desc = (item.itemDescription || item.item_description || item.description || '').toString().toLowerCase();
    return code.includes(query) || brand.includes(query) || model.includes(query) || desc.includes(query);
  });
  
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'items', 'costCentre', 'delivery', 'other', 'accounting'

  const handleAddItem = () => {
    setItems([...items, {
      id: Date.now(),
      code: '',
      description: '',
      qty: '',
      buyPrice: '',
      totalPurchase: '',
      vatablePurchase: '',
      purchaseVat: '',
      sellingPrice: '', 
      totalSellingPrice: '',
      comm: '', 
      opAmount: '', 
      totalOp: '', 
      totalSellingAmount: '',
      vatableSales: '',
      vat: '',
      invoiceAmount: ''
    }]);
  };

  const handleAddCostSectionItem = (section, setSection) => {
    setSection(prev => [...prev, { id: Date.now(), name: '', value: '' }]);
  };

  const confirmDelete = (index, type = 'items') => {
    setItemToDelete(index);
    setDeleteType(type);
    setDeleteConfirmationOpen(true);
  };

  const handleRemoveItem = () => {
    if (itemToDelete !== null) {
      if (deleteType === 'items') {
        const newItems = [...items];
        newItems.splice(itemToDelete, 1);
        setItems(newItems);
      } else if (deleteType === 'costCentre') {
        const newItems = [...costCentreItems];
        newItems.splice(itemToDelete, 1);
        setCostCentreItems(newItems);
      } else if (deleteType === 'delivery') {
        const newItems = [...deliveryDetailsItems];
        newItems.splice(itemToDelete, 1);
        setDeliveryDetailsItems(newItems);
      } else if (deleteType === 'other') {
        const newItems = [...otherCostItems];
        newItems.splice(itemToDelete, 1);
        setOtherCostItems(newItems);
      } else if (deleteType === 'accounting') {
        const newItems = [...accountingAdminItems];
        newItems.splice(itemToDelete, 1);
        setAccountingAdminItems(newItems);
      }
      
      setDeleteConfirmationOpen(false);
      setItemToDelete(null);
      setDeleteType(null);
    }
  };

  const toggleEditMode = (index) => {
      // Deprecated: Rows are always editable now
  };

  const toggleSectionEditMode = (index, section, setSection) => {
      // Deprecated: Rows are always editable now
  };

  const handleNumericInputChange = (index, field, value) => {
    // Allow digits, decimal point, and empty string
    if (/^\d*\.?\d*$/.test(value)) {
        const newItems = [...items];
        newItems[index][field] = value;

        // Perform calculations
        const qty = parseNumber(newItems[index].qty);
        const buyPrice = parseNumber(newItems[index].buyPrice);
        const sellingPrice = parseNumber(newItems[index].sellingPrice);
        const opAmount = parseNumber(newItems[index].opAmount);

        // "Total Purchase" = (Qty * Buy Price)
        const totalPurchase = qty * buyPrice;
        newItems[index].totalPurchase = formatCurrency(totalPurchase);

        // "Vatable Purchase" = (Total Purchase divide by 1.12)
        const vatablePurchase = totalPurchase / 1.12;
        newItems[index].vatablePurchase = formatCurrency(vatablePurchase);

        // "Purchase VAT" = Vatable Purchase * 0.12
        const purchaseVat = vatablePurchase * 0.12;
        newItems[index].purchaseVat = formatCurrency(purchaseVat);

        // "Total Selling Price" = (Selling Price * Qty)
        const totalSellingPrice = sellingPrice * qty;
        newItems[index].totalSellingPrice = formatCurrency(totalSellingPrice);

        // "Total OP Amount" = (Qty multiply by OP Amount)
        const totalOp = qty * opAmount;
        newItems[index].totalOp = formatCurrency(totalOp);

        // "Total Selling Amount" = (Selling Price + OP Amount) * Qty
        const totalSellingAmount = (sellingPrice + opAmount) * qty;
        newItems[index].totalSellingAmount = formatCurrency(totalSellingAmount);

        // "Vatable Sales" = Total Selling Amount / 1.12
        const vatableSales = totalSellingAmount / 1.12;
        newItems[index].vatableSales = formatCurrency(vatableSales);

        // "Vat" = Vatable Sales * 0.12
        const vat = vatableSales * 0.12;
        newItems[index].vat = formatCurrency(vat);

        // "Invoice Amount" = Vatable Sales + Vat
        const invoiceAmount = vatableSales + vat;
        newItems[index].invoiceAmount = formatCurrency(invoiceAmount);

        setItems(newItems);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleInputFocus = (index, field) => {
    const newItems = [...items];
    const val = newItems[index][field];
    if (val) {
        // Remove commas for editing
        const raw = val.toString().replace(/,/g, '');
        newItems[index][field] = raw;
        setItems(newItems);
    }
  };

  const handleInputBlur = (index, field) => {
    const newItems = [...items];
    const val = newItems[index][field];
    if (val && !isNaN(parseFloat(val.toString().replace(/,/g, '')))) {
        const num = parseNumber(val);
        let formatted;
        if (field === 'qty') {
            formatted = formatQuantity(num);
        } else {
            formatted = formatCurrency(num);
        }
        newItems[index][field] = formatted;
        setItems(newItems);
    }
  };

  const handleSectionInputChange = (index, field, value, section, setSection) => {
    const newItems = section.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setSection(newItems);
  };

  const calculateTotalExpenses = () => {
    // Calculate Performance Bond - Removed as per request
    // const actualCostVal = parseNumber(actualProjectCost);
    // const performanceBond = actualCostVal * 0.003;

    // Get Marketing Expense Total (Net of Taxes) - Removed from total as per request
    // const marketingNetTotal = parseNumber(marketingExpense.total);

    const allItems = [
      ...costCentreItems,
      ...deliveryDetailsItems,
      ...otherCostItems,
      ...accountingAdminItems
    ];

    const total = allItems.reduce((sum, item) => {
      // Remove '₱', commas, and spaces, then parse
      const cleanValue = (item.value || '').toString().replace(/[^0-9.-]/g, '');
      const value = parseFloat(cleanValue) || 0;
      return sum + value;
    }, 0);

    const manualMarketingTotal = parseNumber(marketingExpense.manual_total);
    const finalTotal = total + manualMarketingTotal;

    // Add Performance Bond (Removed) and Marketing Net Total (Removed) to total
    return `₱ ${(finalTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateColumnTotal = (field) => {
    const total = items.reduce((sum, item) => {
      return sum + parseNumber(item[field]);
    }, 0);
    
    if (field === 'qty') {
        return formatQuantity(total);
    }
    return formatCurrency(total);
  };

  const handleMarketingExpenseChange = (field, value) => {
    setMarketingExpense(prev => ({ ...prev, [field]: value }));
  };

  const handleMarketingExpenseBlur = (field, value) => {
    const num = parseNumber(value);
    let formatted = '';
    if (num || num === 0) {
      if (field === 'rate') {
          // For rate, don't force 2 decimals. Show integers as is.
          formatted = num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      } else {
          formatted = formatCurrency(num);
      }
    }
    
    // Update the field with formatted value
    setMarketingExpense(prev => {
      const newState = { ...prev, [field]: formatted };
      
      // Calculate Total if dependent fields change
      if (['amount', 'vat', 'income_tax'].includes(field)) {
        const amount = parseNumber(newState.amount);
        const vat = parseNumber(newState.vat);
        const tax = parseNumber(newState.income_tax);
        // Total = Amount - VAT - Tax (Net of taxes)
        const total = amount - vat - tax;
        newState.total = formatCurrency(total);
      }
      return newState;
    });
  };

  const handleMarketingExpense2Change = (field, value) => {
    setMarketingExpense2(prev => ({ ...prev, [field]: value }));
  };

  const handleMarketingExpense2Blur = (field, value) => {
    const num = parseNumber(value);
    let formatted = '';
    if (num || num === 0) {
      formatted = formatCurrency(num);
    }
    
    // Update the field with formatted value
    setMarketingExpense2(prev => {
      const newState = { ...prev, [field]: formatted };
      
      // Calculate Total if dependent fields change
      if (['amount', 'vat', 'income_tax'].includes(field)) {
        const amount = parseNumber(newState.amount);
        const vat = parseNumber(newState.vat);
        const tax = parseNumber(newState.income_tax);
        // Total = Amount - VAT - Tax (As requested by user: amount_2 subtract vat_2 and income tax_2)
        const total = amount - vat - tax;
        newState.total = formatCurrency(total);
      }
      return newState;
    });
  };

  const renderCostSection = (title, items, setItems, type) => {
    let marketingNetTotalValue = 0;
    if (type === 'accounting') {
        marketingNetTotalValue = parseNumber(marketingExpense.total);
    }

    // Determine font sizes and alignments based on section
    let headerClasses = "bg-red-600 text-white font-bold px-2 py-1 mb-3 text-center";
    if (type === 'costCentre') {
        headerClasses += " text-lg -mx-3 -mt-3"; // Adjusted 2 sizes up (from text-sm to text-lg)
    } else if (type === 'delivery' || type === 'other') {
        headerClasses += " text-sm text-left -mx-3"; // Left aligned visually, although text-center might override. Let's force text-left
        headerClasses = headerClasses.replace('text-center', 'text-left');
    } else if (type === 'accounting') {
        // We will hide the header entirely for marketing expense as per instructions
        headerClasses += " hidden";
    } else {
        headerClasses += " text-sm -mx-3";
    }

    return (
    <div className="mb-6">
      <div>
        <div className={headerClasses}>
          {title}
        </div>
        <div className="space-y-1 pl-4 text-sm">
          
          {items.map((item, idx) => (
            <div key={item.id} className="flex justify-between items-center mb-1 group relative">
              <div className="flex-grow pr-4">
                <Input 
                  className="h-7 border-0 border-b border-transparent hover:border-gray-300 focus-visible:border-black rounded-none bg-transparent focus-visible:ring-0 p-0 shadow-none disabled:opacity-100" 
                  value={item.name}
                  onChange={(e) => handleSectionInputChange(idx, 'name', e.target.value, items, setItems)}
                  placeholder="Item Name"
                />
              </div>
              <div className="w-32 relative flex-shrink-0">
                <Input 
                  className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent disabled:opacity-100" 
                  value={item.value}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*\.?\d*$/.test(val)) {
                      handleSectionInputChange(idx, 'value', val, items, setItems);
                    }
                  }}
                  onFocus={(e) => {
                    const val = e.target.value;
                    const raw = val.replace(/[^0-9.]/g, '');
                    handleSectionInputChange(idx, 'value', raw, items, setItems);
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isNaN(parseFloat(val))) {
                      const formatted = `₱ ${parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      handleSectionInputChange(idx, 'value', formatted, items, setItems);
                    }
                  }}
                  placeholder="-"
                />
              </div>
              <div className="absolute -right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!isReadOnly && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 text-red-500"
                    onClick={() => confirmDelete(idx, type)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  )}
              </div>
            </div>
          ))}

          {type === 'accounting' && (
            <div className="flex justify-between items-center mb-1">
              <div className="flex-grow pr-4">
                <span className="font-medium">Marketing Expense</span>
              </div>
              <div className="w-32 relative flex-shrink-0">
                <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                <Input 
                  className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent disabled:opacity-100 font-medium" 
                  value={marketingExpense.manual_total}
                  onChange={(e) => handleMarketingExpenseChange('manual_total', e.target.value)}
                  onBlur={(e) => handleMarketingExpenseBlur('manual_total', e.target.value)}
                  onFocus={(e) => handleCurrencyFocus((val) => handleMarketingExpenseChange('manual_total', val), marketingExpense.manual_total)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {type !== 'accounting' && (
        <div className="flex justify-end mt-2">
          {!isReadOnly && (
          <Button 
            variant="outline" 
            size="sm" 
            className="h-7 text-xs bg-blue-900 hover:bg-blue-800 text-white rounded shadow-sm"
            onClick={() => handleAddCostSectionItem(items, setItems)}
          >
            <Plus className="h-3 w-3 mr-1" /> Add Field
          </Button>
          )}
        </div>
      )}
    </div>
  );
};

  const basicTaxVatRaw = items.reduce((sum, item) => sum + parseNumber(item.vat), 0) - items.reduce((sum, item) => sum + parseNumber(item.purchaseVat), 0);
  const basicTaxVat = isBasicTaxEnabled ? basicTaxVatRaw : 0;
  const lessBirVat = parseNumber(marketingExpense.cred_tax_5);
  const lessBirIncomeTax = parseNumber(marketingExpense.cred_tax_1);

  // NEW COMPUTATIONS FOR PROJECT COST CENTER - COMPUTATION
  const compSalesTotal = items.reduce((sum, item) => sum + parseNumber(item.vatableSales), 0);
  const compCostOfMachine = items.reduce((sum, item) => sum + parseNumber(item.vatablePurchase), 0);
  
  // Update Rebates logic to use TOTAL NET OF TAX
  const meAmount = parseNumber(marketingExpense.total_me_amount);
  const meVat = isMarketingVatEnabled ? (meAmount * 0.12) : 0;
  const meIncomeTax = isMarketingIncomeTaxEnabled ? (meAmount * 0.25) : 0;
  const meNetOfTax = meAmount - meVat - meIncomeTax;
  const compRebates = meNetOfTax; // Now uses the calculation from Dictated Amount
  
  const compCostOfSalesTotal = compCostOfMachine + compRebates;
  const compGrossProfit = compSalesTotal - compCostOfSalesTotal;
  const compGrossProfitPercentage = compSalesTotal !== 0 ? (compGrossProfit / compSalesTotal) * 100 : 0;
  
  const compOperatingExpense = [...costCentreItems, ...deliveryDetailsItems, ...otherCostItems, ...accountingAdminItems].reduce((sum, item) => {
    const cleanValue = (item.value || '').toString().replace(/[^0-9.-]/g, '');
    return sum + (parseFloat(cleanValue) || 0);
  }, 0);
  
  const compNetProfit = compGrossProfit - compOperatingExpense;

  const basicTaxIncomeTaxRaw = compNetProfit * 0.25;
  const basicTaxIncomeTax = isBasicTaxEnabled ? basicTaxIncomeTaxRaw : 0;

  const taxesPayableVatRaw = basicTaxVat - lessBirVat;
  const taxesPayableIncomeTaxRaw = basicTaxIncomeTax - lessBirIncomeTax;

  const taxesPayableVat = Math.max(0, taxesPayableVatRaw);
  const taxesPayableIncomeTax = Math.max(0, taxesPayableIncomeTaxRaw);
  const taxesPayableTotal = taxesPayableVat + taxesPayableIncomeTax;

  const taxesExposureVat = meAmount * 0.12;
  const taxesExposureIncomeTax = meAmount * 0.25;

  const totalVat = taxesExposureVat + taxesPayableVat;
  const totalIncomeTax = taxesExposureIncomeTax + taxesPayableIncomeTax;
  const grandTotal = totalVat + totalIncomeTax;

  const compNetProfitAfterTax = compNetProfit - taxesPayableTotal;
  const compNetProfitAfterTaxPercentage = compSalesTotal !== 0 ? (compNetProfitAfterTax / compSalesTotal) * 100 : 0;
  const compTaxOnRebatesTotal = taxesExposureVat + taxesExposureIncomeTax;
  const compNetProfitAfterTaxOnRebates = compNetProfitAfterTax - compTaxOnRebatesTotal;
  const compNetProfitAfterTaxOnRebatesPercentage = compSalesTotal !== 0 ? (compNetProfitAfterTaxOnRebates / compSalesTotal) * 100 : 0;

  // PROJECT COST COMPUTATION (NEW TABLE)
  const newCompTotalSellingPrice = items.reduce((sum, item) => sum + parseNumber(item.totalSellingPrice), 0);
  const newCompTotalPurchase = items.reduce((sum, item) => sum + parseNumber(item.totalPurchase), 0);
  const newCompGrossProfit = (newCompTotalSellingPrice - newCompTotalPurchase) / 1.12;

  const newCompTotalOpTable = items.reduce((sum, item) => sum + parseNumber(item.totalOp), 0);
  const newCompTotalOp = newCompTotalOpTable / 1.12;

  const newCompGrossProfitAfterOp = newCompGrossProfit + newCompTotalOp;

  const totalMeAmountNum = parseNumber(marketingExpense.total_me_amount);
  const newCompRebates = totalMeAmountNum - (isMarketingVatEnabled ? totalMeAmountNum * 0.12 : 0) - (isMarketingIncomeTaxEnabled ? totalMeAmountNum * 0.25 : 0);

  const newCompExpenses = compOperatingExpense;

  const newCompNetProfitBeforeTax = newCompGrossProfitAfterOp - newCompRebates - newCompExpenses;

  const taxesPayableVatForNetProfit = isNetProfitVatEnabled ? taxesPayableVat : 0;
  const taxesPayableIncomeTaxForNetProfit = isNetProfitIncomeTaxEnabled ? taxesPayableIncomeTax : 0;

  const newCompNetProfitAfterTax = newCompNetProfitBeforeTax - taxesPayableVatForNetProfit - taxesPayableIncomeTaxForNetProfit;
  
  const newCompNetProfitAfterTaxOnRebatesFinal = newCompNetProfitAfterTax - taxesExposureVat - taxesExposureIncomeTax;

  // Export to PDF Function
  const handleExportPDF = async () => {
    const originalElement = document.getElementById('pdf-content-container');
    if (!originalElement) return;
    const exportWidth = Math.max(
      2100,
      Math.ceil(originalElement.scrollWidth || 0),
      Math.ceil(originalElement.offsetWidth || 0)
    );

    // 1. Clone the element
    const clone = originalElement.cloneNode(true);
    
    // 2. Remove buttons from the clone
    const buttons = clone.querySelectorAll('button');
    buttons.forEach(btn => btn.remove());
    
    // Remove the specific action div that contains the save/export buttons
    const actionDiv = clone.querySelector('.flex.justify-end.mt-4.gap-2');
    if (actionDiv) actionDiv.remove();

    // Copy input values from original to clone
    const originalInputs = originalElement.querySelectorAll('input, textarea, select');
    const clonedInputs = clone.querySelectorAll('input, textarea, select');
    originalInputs.forEach((input, index) => {
      if (clonedInputs[index]) {
        if (input.type === 'checkbox' || input.type === 'radio') {
          clonedInputs[index].checked = input.checked;
        } else {
          clonedInputs[index].value = input.value;
        }
      }
    });

    originalInputs.forEach((input, index) => {
      const clonedInput = clonedInputs[index];
      if (!clonedInput) return;

      const tag = (input.tagName || '').toLowerCase();
      const type = (input.type || '').toLowerCase();

      if (type === 'checkbox' || type === 'radio') return;

      let textValue = '';
      if (tag === 'select') {
        const sel = input;
        textValue = sel.options?.[sel.selectedIndex]?.text ?? sel.value ?? '';
      } else {
        textValue = input.value ?? '';
      }

      const span = document.createElement('span');
      span.textContent = textValue;
      span.className = clonedInput.className || '';

      const computedStyle = window.getComputedStyle(input);
      span.style.display = 'flex';
      span.style.alignItems = 'center';
      span.style.justifyContent =
        computedStyle.textAlign === 'right'
          ? 'flex-end'
          : computedStyle.textAlign === 'center'
            ? 'center'
            : 'flex-start';
      span.style.width = '100%';
      const isMultiline = tag === 'textarea';
      if (isMultiline) {
        span.style.height = 'auto';
      } else {
        const measuredHeight = Math.ceil(input.getBoundingClientRect().height);
        if (measuredHeight > 0) {
          span.style.height = `${measuredHeight}px`;
          span.style.minHeight = `${measuredHeight}px`;
        } else if (computedStyle.height && computedStyle.height !== 'auto') {
          span.style.height = computedStyle.height;
          span.style.minHeight = computedStyle.height;
        }
      }
      span.style.paddingTop = computedStyle.paddingTop;
      span.style.paddingRight = computedStyle.paddingRight;
      span.style.paddingBottom = computedStyle.paddingBottom;
      span.style.paddingLeft = computedStyle.paddingLeft;
      span.style.fontFamily = computedStyle.fontFamily;
      span.style.fontSize = computedStyle.fontSize;
      span.style.fontWeight = computedStyle.fontWeight;
      span.style.lineHeight = computedStyle.lineHeight;
      span.style.color = computedStyle.color;
      span.style.backgroundColor = computedStyle.backgroundColor;
      span.style.boxSizing = computedStyle.boxSizing;
      span.style.borderTop = computedStyle.borderTop;
      span.style.borderRight = computedStyle.borderRight;
      span.style.borderBottom = computedStyle.borderBottom;
      span.style.borderLeft = computedStyle.borderLeft;
      span.style.borderRadius = computedStyle.borderRadius;
      span.style.boxShadow = computedStyle.boxShadow;
      span.style.whiteSpace = isMultiline ? 'pre-wrap' : 'nowrap';
      span.style.overflow = isMultiline ? 'visible' : 'hidden';
      span.style.textOverflow = 'clip';
      span.style.wordBreak = isMultiline ? 'break-word' : 'normal';

      if (clonedInput.parentNode) {
        clonedInput.parentNode.replaceChild(span, clonedInput);
      }
    });

    // Remove toggle switch controls from the export clone.
    const toggleLabels = clone.querySelectorAll('label');
    toggleLabels.forEach((label) => {
      if (label.querySelector('input.sr-only.peer')) {
        label.remove();
      }
    });

    // 3. Append clone to body so it gets CSS applied (hidden from view)
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.width = `${exportWidth}px`;
    clone.style.minWidth = `${exportWidth}px`;
    clone.style.maxWidth = `${exportWidth}px`;
    clone.style.margin = '0';
    clone.style.transform = 'none';
    clone.style.zoom = '1';
    clone.style.backgroundColor = 'white';
    
    document.body.appendChild(clone);

    // Keep the export visually close to the on-screen layout while avoiding clipping.
    const style = document.createElement('style');
    style.innerHTML = `
      #pdf-content-container {
        background: white !important;
      }

      #pdf-content-container fieldset {
        border: none !important;
        box-shadow: none !important;
      }

      #pdf-content-container .grid.grid-cols-12.items-center.gap-4 label,
      #pdf-content-container .grid.grid-cols-12.items-center.gap-2 label {
        white-space: nowrap !important;
      }

      #pdf-content-container .grid.grid-cols-12.items-center.gap-4 .col-span-3 {
        grid-column: span 2 / span 2 !important;
      }

      #pdf-content-container .grid.grid-cols-12.items-center.gap-4 .col-span-9 {
        grid-column: span 10 / span 10 !important;
      }

      #pdf-content-container table,
      #pdf-content-container tr,
      #pdf-content-container td,
      #pdf-content-container th {
        overflow: visible !important;
        word-break: break-word !important;
      }

      #pdf-content-container input,
      #pdf-content-container textarea,
      #pdf-content-container select {
        text-overflow: clip !important;
      }
    `;
    document.head.appendChild(style);

    // Give browser a moment to apply styles
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const exportHeight = Math.ceil(clone.scrollHeight || clone.offsetHeight || originalElement.scrollHeight || originalElement.offsetHeight || 0);

      // 4. Use html2canvas to capture the clone
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: exportWidth,
        windowHeight: exportHeight,
        width: exportWidth,
        height: exportHeight,
        scrollX: 0,
        scrollY: 0
      });

      // 5. Export the full captured page across A4 portrait pages
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8; // 8mm margin
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2);
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, 'JPEG', margin, position, contentWidth, imgHeight);
      heightLeft -= contentHeight;

      while (heightLeft > 0) {
        position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', margin, position, contentWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      pdf.save(`Project_Computation_${pccNumber || 'Details'}.pdf`);
      toast.success("PDF exported successfully.");

      if (currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com' && project?.project_detail_id && !isExportedByOam) {
        const now = new Date().toISOString();
        try {
          await axiosInstance.put(`/ccp/project-details/${project.project_detail_id}`, {
            is_exported_by_oam: true,
            exported_by_oam_at: now
          });
          setIsExportedByOam(true);
          setExportedByOamAt(now);
          window.dispatchEvent(new Event('projectUpdated'));
        } catch (e) {
          console.error('Failed to mark project as exported by OAM:', e);
        }
      }
      
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF.");
    } finally {
      // Cleanup
      document.body.removeChild(clone);
      document.head.removeChild(style);
    }
  };

  return (
    <TooltipProvider>
      <div id="pdf-content-container" className="relative space-y-6 max-w-[2100px] mx-auto pb-10">
      {/* Saving Overlay */}
      {isSaving && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl">
            {saveSuccess ? (
              <>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-lg font-semibold text-gray-800">Saved Successfully!</p>
              </>
            ) : (
              <>
                <div className="h-12 w-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-lg font-semibold text-gray-800">Saving...</p>
              </>
            )}
          </div>
        </div>
      )}

      {isDisapprovedCosts && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="text-red-600/20 font-extrabold tracking-widest select-none" style={{ transform: 'rotate(-20deg)', fontSize: '120px', lineHeight: 1 }}>
            DISAPPROVED
          </div>
        </div>
      )}
      {/* Navigation Back */}
      <Button variant="ghost" onClick={onBack} className="mb-4 pl-0 hover:pl-2 transition-all">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Project List
      </Button>

      <fieldset disabled={isReadOnly} className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-slate-800 block">
        {/* Header Section */}
        <div className="grid grid-cols-12 gap-6 mb-8">
          {/* Left Column Fields */}
          <div className="col-span-12 md:col-span-7 space-y-3">
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">PCC NUMBER</label>
              <Input 
                className="col-span-9 h-8 bg-gray-100 dark:bg-gray-800" 
                placeholder="Auto-generated" 
                value={pccNumber ? pccNumber.toUpperCase() : ''}
                readOnly
                disabled
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">DATE SUBMITTED</label>
              <Input 
                type={isDateSubmittedFocused ? "date" : "text"} 
                className="col-span-9 h-8" 
                value={isDateSubmittedFocused ? dateSubmitted : formatDisplayDate(dateSubmitted)}
                onChange={(e) => setDateSubmitted(e.target.value)}
                onFocus={() => setIsDateSubmittedFocused(true)}
                onBlur={() => setIsDateSubmittedFocused(false)}
                placeholder="Select Date"
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">DATE NEEDED</label>
              <Input 
                type={isDateNeededFocused ? "date" : "text"} 
                className="col-span-9 h-8" 
                value={isDateNeededFocused ? dateNeeded : formatDisplayDate(dateNeeded)}
                onChange={(e) => setDateNeeded(e.target.value)}
                onFocus={() => setIsDateNeededFocused(true)}
                onBlur={() => setIsDateNeededFocused(false)}
                placeholder="Select Date"
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">PROJECT NAME</label>
              <Input 
                className="col-span-9 h-8" 
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">PROJECT TYPE</label>
              <select 
                className="col-span-9 h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-gray-200 dark:border-gray-700 dark:bg-slate-950"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
              >
                <option value="" disabled>Select Project Type</option>
                <option value="BIDDING">BIDDING</option>
                <option value="RFQ">RFQ</option>
                <option value="NEGOTIATED">NEGOTIATED</option>
                <option value="OPPORTUNISTIC">OPPORTUNISTIC</option>
                <option value="CONSUMABLES">CONSUMABLES</option>
              </select>
            </div>
            <div className="grid grid-cols-12 items-center gap-4 mt-6">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">END USER</label>
              <Input 
                className="col-span-9 h-8" 
                value={endUser}
                onChange={(e) => setEndUser(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">BDO</label>
              <Input 
                className="col-span-9 h-8" 
                value={bdoName}
                onChange={(e) => setBdoName(e.target.value)}
                disabled 
              />
            </div>
          </div>

          {/* Right Column Fields */}
          <div className="col-span-12 md:col-span-5 space-y-3">
            <div className="grid grid-cols-12 items-center gap-2">
              <label className="col-span-6 text-sm font-semibold text-right pr-2 text-gray-700 dark:text-gray-300">TOTAL PROJECT COST / ABC</label>
              <div className="col-span-6 relative">
                <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                <Input 
                  className="h-8 pl-6 text-right" 
                  placeholder="0.00" 
                  value={totalProjectCost}
                  onChange={(e) => setTotalProjectCost(e.target.value)}
                  onBlur={() => handleCurrencyBlur(setTotalProjectCost, totalProjectCost)}
                  onFocus={() => handleCurrencyFocus(setTotalProjectCost, totalProjectCost)}
                />
              </div>
            </div>
            <div className="grid grid-cols-12 items-center gap-2">
              <label className="col-span-6 text-sm font-semibold text-right pr-2 text-gray-700 dark:text-gray-300">ACTUAL PROJECT COST</label>
              <div className="col-span-6 relative">
                <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                <Input 
                  className="h-8 pl-6 text-right bg-gray-50 dark:bg-gray-800" 
                  placeholder=" -" 
                  value={actualProjectCost}
                  readOnly
                  disabled
                />
              </div>
            </div>
            <div className="flex justify-end mt-8">
               <div className="flex items-center gap-2">
                  {/* Status and Save button removed from here */}
               </div>
            </div>
          </div>
        </div>

        {/* Main Items Table */}
        <div className="mb-4 border border-gray-300 dark:border-gray-700 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-900 text-white">
                <th className="p-3 text-left font-medium border-r border-blue-800 w-64">Item Code</th>
                <th className="p-3 text-left font-medium border-r border-blue-800 w-1/6">Item Description</th>
                <th className="p-3 text-center font-medium border-r border-blue-800">Qty</th>
                  <th className="p-3 text-right font-medium border-r border-blue-800">Buy Price</th>
                  <th className="p-3 text-right font-medium border-r border-blue-800">Total Purchase</th>
                  <th className="p-3 text-right font-medium border-r border-blue-800">Vatable Purchase</th>
                  <th className="p-3 text-right font-medium border-r border-blue-800">VAT</th>
                  <th className="p-3 text-right font-medium border-r border-blue-800">Selling Price</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Total Selling<br/>Price</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">OP Amount</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Total OP<br/>Amount</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Total Selling<br/>Amount</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Vatable Sales</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">VAT</th>
                <th className="p-3 text-right font-medium">Invoice Amount</th>
                <th className="p-3 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-slate-800">
              {items.map((item, i) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-1">
                      <Input 
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 rounded-none shadow-none disabled:opacity-100 disabled:cursor-default flex-1 min-w-0" 
                        value={item.code}
                        onChange={(e) => handleInputChange(i, 'code', e.target.value)}
                      />
                      {!isReadOnly && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 shrink-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openInventorySearchModal(i)}
                          title="Search Inventory"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.description}
                      onChange={(e) => handleInputChange(i, 'description', e.target.value)}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                      <Input
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 text-center rounded-none shadow-none disabled:opacity-100 disabled:cursor-default"
                        value={item.qty}
                        onChange={(e) => handleNumericInputChange(i, 'qty', e.target.value)}
                        onFocus={() => handleInputFocus(i, 'qty')}
                        onBlur={() => handleInputBlur(i, 'qty')}
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                      <Input
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default"   
                        value={item.buyPrice}
                        onChange={(e) => handleNumericInputChange(i, 'buyPrice', e.target.value)}
                        onFocus={() => handleInputFocus(i, 'buyPrice')}
                        onBlur={() => handleInputBlur(i, 'buyPrice')}
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                      <Input
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default"   
                        value={item.totalPurchase}
                        disabled
                        readOnly
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                      <Input
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default"   
                        value={item.vatablePurchase}
                        disabled
                        readOnly
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                      <Input
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default"   
                        value={item.purchaseVat}
                        disabled
                        readOnly
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.sellingPrice}
                      onChange={(e) => handleNumericInputChange(i, 'sellingPrice', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'sellingPrice')}
                      onBlur={() => handleInputBlur(i, 'sellingPrice')}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.totalSellingPrice}
                      onChange={(e) => handleInputChange(i, 'totalSellingPrice', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'totalSellingPrice')}
                      onBlur={() => handleInputBlur(i, 'totalSellingPrice')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.opAmount}
                      onChange={(e) => handleNumericInputChange(i, 'opAmount', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'opAmount')}
                      onBlur={() => handleInputBlur(i, 'opAmount')}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.totalOp}
                      onChange={(e) => handleInputChange(i, 'totalOp', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'totalOp')}
                      onBlur={() => handleInputBlur(i, 'totalOp')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.totalSellingAmount}
                      onChange={(e) => handleInputChange(i, 'totalSellingAmount', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'totalSellingAmount')}
                      onBlur={() => handleInputBlur(i, 'totalSellingAmount')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.vatableSales}
                      onChange={(e) => handleInputChange(i, 'vatableSales', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'vatableSales')}
                      onBlur={() => handleInputBlur(i, 'vatableSales')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.vat}
                      onChange={(e) => handleInputChange(i, 'vat', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'vat')}
                      onBlur={() => handleInputBlur(i, 'vat')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.invoiceAmount}
                      onChange={(e) => handleInputChange(i, 'invoiceAmount', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'invoiceAmount')}
                      onBlur={() => handleInputBlur(i, 'invoiceAmount')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 text-center flex items-center justify-center space-x-1">
                    {!isReadOnly && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => confirmDelete(i)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="bg-blue-900 text-white font-bold">
                  <td colSpan={2} className="p-3 border-r border-blue-800">TOTAL</td>
                  <td className="p-3 border-r border-blue-800 text-center">{calculateColumnTotal('qty')}</td>
                  <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('buyPrice')}</td>
                  <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalPurchase')}</td>
                  <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('vatablePurchase')}</td>
                  <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('purchaseVat')}</td>
                  <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('sellingPrice')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalSellingPrice')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('opAmount')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalOp')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalSellingAmount')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('vatableSales')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('vat')}</td>
                <td className="p-3 text-right">{calculateColumnTotal('invoiceAmount')}</td>
                <td className="p-3"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mb-8">
            {!isReadOnly && (
            <Button onClick={handleAddItem} variant="default" size="sm" className="h-7 text-xs bg-blue-900 hover:bg-blue-800 text-white rounded shadow-sm">
                <Plus className="h-3 w-3 mr-1" />
                Add Field
            </Button>
            )}
        </div>

        {/* Notes/Remarks */}
        <div className="mb-8">
          <div className="bg-blue-900 text-white px-3 py-1 font-bold text-sm mb-0 rounded-t-sm">NOTES/REMARKS</div>
          <textarea 
            className="w-full border border-gray-300 dark:border-gray-700 p-2 min-h-[80px] bg-blue-50/30 dark:bg-slate-800/30 rounded-b-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          ></textarea>
        </div>

        {/* Bottom Split Section */}
        <div className="pdf-page-2-container mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left Side: Cost Centre */}
            <div className="space-y-4">
              <div className="border border-black dark:border-gray-500 bg-white dark:bg-slate-800 p-3 h-fit">
                {renderCostSection("COST CENTRE", costCentreItems, setCostCentreItems, 'costCentre')}
                {renderCostSection("DELIVERY DETAILS", deliveryDetailsItems, setDeliveryDetailsItems, 'delivery')}
                {renderCostSection("OTHER COST", otherCostItems, setOtherCostItems, 'other')}
                {renderCostSection("MARKETING EXPENSE", accountingAdminItems, setAccountingAdminItems, 'accounting')}

                {/* Total Expenses */}
                <div className="border-t border-black dark:border-gray-500 pt-4 pb-2 -mx-3 px-3 mt-2">
                   <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-red-600">TOTAL EXPENSES</span>
                      <span className="text-2xl font-bold text-black dark:text-white">{calculateTotalExpenses()}</span>
                   </div>
                </div>
              </div>

              {/* Marketing Expense Computation Table */}
            {currentUser?.email === 'vinsimon23@gmail.com' && (
            <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
               <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-lg">Marketing Expense Computation</div>
                 
                 <p className="text-xs font-bold italic mb-2">Collection (net of 6% WHT and 1% Retention, if any)</p>
                 
                 <div className="space-y-1 pl-4 text-sm">
                    <div className="flex justify-between items-center mb-1">
                       <div className="flex justify-between items-center w-1/2">
                         <span>Amount Less:</span>
                       </div>
                       <div className="w-32 relative">
                          <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                          <Input 
                              className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={marketingExpense2.amount}
                              readOnly
                          />
                       </div>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                       <div className="flex justify-between items-center w-1/2">
                         <span>12% VAT</span>
                         <label className="relative inline-flex items-center cursor-pointer mr-2" title="Zero out VAT">
                           <input 
                             type="checkbox" 
                             className="sr-only peer" 
                             checked={isMarketing2VatZeroed}
                             onChange={() => setIsMarketing2VatZeroed(!isMarketing2VatZeroed)}
                           />
                           <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                         </label>
                       </div>
                       <div className="w-32 relative">
                          <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                          <Input 
                              className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={marketingExpense2.vat}
                              readOnly
                          />
                       </div>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                       <div className="flex justify-between items-center w-1/2">
                         <span>Income Tax</span>
                         <label className="relative inline-flex items-center cursor-pointer mr-2" title="Zero out Tax">
                           <input 
                             type="checkbox" 
                             className="sr-only peer" 
                             checked={isMarketing2TaxZeroed}
                             onChange={() => setIsMarketing2TaxZeroed(!isMarketing2TaxZeroed)}
                           />
                           <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                         </label>
                       </div>
                       <div className="w-32 relative">
                          <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                          <Input 
                              className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={marketingExpense2.income_tax}
                              readOnly
                          />
                       </div>
                    </div>
                    <div className="flex justify-between font-bold pt-1 items-center">
                       <div className="flex justify-between items-center w-1/2">
                         <span>TOTAL NET OF TAXES</span>
                       </div>
                       <div className="w-32 relative">
                          <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                          <Input 
                              className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={marketingExpense2.total}
                              readOnly
                          />
                       </div>
                    </div>
                 </div>
              </div>
              )}
            </div>

            {/* Middle Side: BIR MARKETING EXPENSE EXPOSURE */}
            <div className="space-y-4">
               <div className="mb-6">
                 <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
                   <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-sm">
                     BIR MARKETING EXPENSE EXPOSURE
                   </div>
                   
                   <div className="w-full text-sm">
                     {/* Header 1 */}
                     <div className="flex font-bold border-b border-gray-300 dark:border-gray-600 pb-1 mb-1 text-center">
                       <div className="w-1/4"></div>
                       <div className="w-1/4">VAT</div>
                       <div className="w-1/4">Income Tax</div>
                       <div className="w-1/4">Total</div>
                     </div>
                     
                     {/* Header 2 */}
                     <div className="flex font-bold border-b border-gray-300 dark:border-gray-600 pb-1 mb-2 text-center text-red-600">
                       <div className="w-1/4"></div>
                       <div className="w-1/4">12%</div>
                       <div className="w-1/4">25%</div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Data Row */}
                     <div className="flex items-center text-right mt-2">
                       <div className="w-1/4 font-bold text-left pr-2 text-xs">Taxes Exposure</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(taxesExposureVat)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(taxesExposureIncomeTax)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 font-bold">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(taxesExposureVat + taxesExposureIncomeTax)}</span>
                       </div>
                     </div>

                     {/* Empty Row */}
                     <div className="h-4 w-full"></div>

                     {/* BIR Basic Taxes Payable Header */}
                     <div className="w-full text-center font-bold bg-red-600 text-white pb-1 mb-2 text-lg">
                       BIR Basic Taxes Payable
                     </div>

                     {/* Basic Tax Row */}
                     <div className="flex items-center text-right mb-2">
                       <div className="w-1/4 font-bold text-left pr-2 text-xs">Basic Tax</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(basicTaxVat)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(basicTaxIncomeTax)}</span>
                       </div>
                      <div className="w-1/4 flex justify-end">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={isBasicTaxEnabled}
                            onChange={() => setIsBasicTaxEnabled(!isBasicTaxEnabled)}
                          />
                          <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                     </div>

                     {/* Less BIR 2307 Row */}
                     <div className="flex items-center text-right mb-2">
                       <div className="w-1/4 font-bold text-left pr-2 text-xs">Less BIR 2307</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(lessBirVat)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(lessBirIncomeTax)}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Taxes Payable Row */}
                     <div className="flex items-center text-right mb-2">
                       <div className="w-1/4 font-bold text-left pr-2 text-xs text-black dark:text-white">Taxes Payable</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(taxesPayableVatRaw)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(taxesPayableIncomeTaxRaw)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 font-bold">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(taxesPayableTotal)}</span>
                       </div>
                     </div>

                     {/* Empty Row */}
                     <div className="h-4 w-full"></div>

                     {/* TOTAL Row */}
                     <div className="flex items-center text-right mb-2">
                       <div className="w-1/4 font-bold text-left pr-2 text-xs text-black dark:text-white">TOTAL</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(totalVat)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(totalIncomeTax)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 font-bold">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(grandTotal)}</span>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>

               {/* New Table: PROJECT COST CENTER - COMPUTATION */}
               <div className="mb-6">
                 <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
                   <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-sm">
                     PROJECT COST CENTER - COMPUTATION
                   </div>
                   
                   <div className="w-full text-sm">
                     {/* Sales Row */}
                     <div className="flex items-center border-b border-black pb-1 mb-2">
                       <div className="w-1/4 text-left pl-4">SALES</div>
                       <div className="w-1/4"></div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compSalesTotal)}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Cost of Sales Row */}
                     <div className="flex items-center mb-2 mt-2">
                       <div className="text-center font-bold mb-2 w-full">COST OF SALES</div>
                     </div>

                     {/* Cost of Machine Row */}
                     <div className="flex items-center mb-1">
                       <div className="w-1/4 text-left pl-4">Cost of Machine</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compCostOfMachine)}</span>
                       </div>
                       <div className="w-1/4"></div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Rebates Row */}
                     <div className="flex items-center mb-1 border-b border-black pb-1">
                       <div className="w-1/4 text-left pl-4">Rebates</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(compRebates)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 items-center pt-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(compCostOfSalesTotal)}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Empty Row */}
                     <div className="h-4 w-full"></div>

                     {/* GROSS PROFIT Row */}
                     <div className="flex items-center mb-1">
                       <div className="w-1/4 text-left pl-4">GROSS PROFIT</div>
                       <div className="w-1/4"></div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 pt-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compGrossProfit)}</span>
                       </div>
                       <div className="w-1/4 text-right font-bold">
                         {compGrossProfitPercentage.toFixed(2)}%
                       </div>
                     </div>

                     {/* OPERATING EXPENSE Row */}
                     <div className="flex items-center mb-1">
                       <div className="w-2/4 text-left pl-4">OPERATING EXPENSE</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compOperatingExpense)}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Empty Row */}
                     <div className="h-4 w-full"></div>

                     {/* NET PROFIT Row */}
                     <div className="flex items-center mb-4">
                       <div className="w-1/4 text-left pl-4">NET PROFIT</div>
                       <div className="w-1/4"></div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 pt-1 font-bold">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compNetProfit)}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* LESS: BASIC TAX Title */}
                     <div className="text-center font-bold mb-2 w-full">
                       LESS: BASIC TAX
                     </div>

                     {/* Empty Row */}
                     <div className="h-2 w-full"></div>

                     {/* VAT Row */}
                     <div className="flex items-center mb-1">
                       <div className="w-1/4 text-left pl-4">VAT</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(taxesPayableVat < 0 ? 0 : taxesPayableVat)}</span>
                       </div>
                       <div className="w-1/4"></div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Income Tax Row */}
                     <div className="flex items-center mb-1 border-b border-black pb-1">
                       <div className="w-1/4 text-left pl-4">Income Tax</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(taxesPayableIncomeTax < 0 ? 0 : taxesPayableIncomeTax)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 items-center pt-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency((taxesPayableVat < 0 ? 0 : taxesPayableVat) + (taxesPayableIncomeTax < 0 ? 0 : taxesPayableIncomeTax))}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Empty Row */}
                     <div className="h-4 w-full"></div>

                     {/* NET PROFIT AFTER TAX Row */}
                     <div className="flex items-center mb-2">
                       <div className="w-2/4 text-left pl-4">NET PROFIT AFTER TAX</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 pt-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compNetProfitAfterTax)}</span>
                       </div>
                       <div className="w-1/4 text-right font-bold">
                         {compNetProfitAfterTaxPercentage.toFixed(2)}%
                       </div>
                     </div>

                     {/* LESS: TAX ON REBATES Row */}
                     <div className="flex items-center mb-1">
                       <div className="text-center font-bold mb-2 w-full">LESS: TAX ON REBATES</div>
                     </div>

                     {/* VAT under Tax on Rebates Row */}
                     <div className="flex items-center mb-1">
                       <div className="w-1/4 text-left pl-4">VAT</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(taxesExposureVat)}</span>
                       </div>
                       <div className="w-1/4"></div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Income Tax under Tax on Rebates Row */}
                     <div className="flex items-center mb-1 border-b border-black pb-1">
                       <div className="w-1/4 text-left pl-4">Income Tax</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(taxesExposureIncomeTax)}</span>
                       </div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 items-center pt-1">
                         <span className="font-bold text-xs">₱</span>
                         <span>{formatCurrency(compTaxOnRebatesTotal)}</span>
                       </div>
                       <div className="w-1/4"></div>
                     </div>

                     {/* Empty Row */}
                     <div className="h-4 w-full"></div>

                     {/* NET PROFIT AFTER TAX ON REBATES Row */}
                     <div className={`flex items-center mb-2 p-1 ${compNetProfitAfterTaxOnRebatesPercentage >= 20 ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                       <div className="w-2/4 font-bold text-left text-xs sm:text-sm">NET PROFIT AFTER TAX ON REBATES</div>
                       <div className="w-1/4 px-1 flex justify-end gap-1 font-bold pt-1">
                         <span className="font-bold text-xs mt-0.5">₱</span>
                         <span>{formatCurrency(compNetProfitAfterTaxOnRebates)}</span>
                       </div>
                       <div className="w-1/4 text-right font-bold">
                         {compNetProfitAfterTaxOnRebatesPercentage.toFixed(2)}%
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
            </div>

            {/* Right Side: Marketing Expenses */}
          <div className="space-y-6">

            {/* Computation 1 */}
            <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
               <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-lg">MARKETING EXPENSE (ME)</div>
               
               <div className="flex justify-between items-center font-bold mb-2">
                  <span>Invoice Amount</span>
                  <div className="w-32 relative">
                     <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                     <Input 
                         className="h-7 pl-6 text-right text-sm font-bold border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                         value={formatCurrency(items.reduce((sum, item) => sum + parseNumber(item.invoiceAmount), 0))}
                         readOnly
                     />
                  </div>
               </div>

               <p className="text-xs font-bold italic mb-2">Deduction</p>
               
               <div className="space-y-1 pl-4 text-sm">
                  <div className="flex justify-between items-center mb-1">
                     <div className="flex justify-between items-center w-1/2">
                       <span>1% Cred Tax</span>
                       <label className="relative inline-flex items-center cursor-pointer mr-2">
                         <input 
                           type="checkbox" 
                           className="sr-only peer" 
                           checked={isCredTax1Enabled}
                           onChange={() => setIsCredTax1Enabled(!isCredTax1Enabled)}
                         />
                         <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                       </label>
                     </div>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.cred_tax_1}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <div className="flex justify-between items-center w-1/2">
                       <span>5% Cred Tax</span>
                       <label className="relative inline-flex items-center cursor-pointer mr-2">
                         <input 
                           type="checkbox" 
                           className="sr-only peer" 
                           checked={isCredTax5Enabled}
                           onChange={() => setIsCredTax5Enabled(!isCredTax5Enabled)}
                         />
                         <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                       </label>
                     </div>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.cred_tax_5}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                    <div className="flex justify-between items-center mb-1">
                       <span 
                          className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                          onClick={() => {
                              if (!isReadOnly) {
                                  setTempRetentionRate(marketingExpense.retention_rate);
                                  setRetentionModalOpen(true);
                              }
                          }}
                       >
                           Retention ({marketingExpense.retention_rate}%)
                       </span>
                       <div className="w-32 relative">
                          <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                          <Input 
                              className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={marketingExpense.retention_amount}
                              readOnly
                              disabled
                          />
                       </div>
                    </div>

                    {!isReadOnly && (
                      <div className="flex justify-start mb-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setMeManualDeductionRows(prev => ([...prev, { label: '', amount: '' }]))}
                        >
                          +
                        </Button>
                      </div>
                    )}

                    {meManualDeductionRows.map((row, idx) => (
                      <div key={idx} className="relative group">
                        <div className="flex justify-between items-center mb-1">
                          <div className="w-1/2 pr-2">
                            <Input
                              className="h-7 text-left text-sm border-0 rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={row.label || ''}
                              onChange={(e) => {
                                const nextLabel = e.target.value;
                                setMeManualDeductionRows(prev => prev.map((r, i) => (i === idx ? { ...r, label: nextLabel } : r)));
                              }}
                            />
                          </div>
                          <div className="w-32 relative">
                            <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                            <Input
                              className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                              value={row.amount || ''}
                              onChange={(e) => {
                                const raw = e.target.value || '';
                                let next = raw.replace(/[^0-9.]/g, '');
                                const parts = next.split('.');
                                if (parts.length > 2) next = `${parts[0]}.${parts.slice(1).join('')}`;
                                setMeManualDeductionRows(prev => prev.map((r, i) => (i === idx ? { ...r, amount: next } : r)));
                              }}
                              onFocus={() => {
                                const next = (row.amount || '').toString().replace(/,/g, '');
                                setMeManualDeductionRows(prev => prev.map((r, i) => (i === idx ? { ...r, amount: next } : r)));
                              }}
                              onBlur={() => {
                                const num = parseNumber(row.amount);
                                const formatted = row.amount ? formatCurrency(num) : '';
                                setMeManualDeductionRows(prev => prev.map((r, i) => (i === idx ? { ...r, amount: formatted } : r)));
                              }}
                            />
                          </div>
                        </div>

                        {!isReadOnly && (
                          <div className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-500"
                              onClick={() => setMeManualDeductionRows(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}

                  <div className="flex justify-between items-center mb-1">
                     <span>Total Deduction</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.total_deduction}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center font-bold pt-1 mb-1">
                     <span>Net of Collection</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.net_of_collection}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                        <span>ME Rate</span>
                        <div className="w-32 relative">
                           <Input 
                               className={`h-7 pr-2 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent ${parseFloat(marketingExpense.me_rate) > 20 ? 'text-red-500 font-bold' : ''}`}
                               value={marketingExpense.me_rate}
                               readOnly
                           />
                        </div>
                     </div>
                  <div className="flex justify-between font-bold pt-1 items-center">
                     <span>Total ME Amount</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.total_me_amount}
                            readOnly
                        />
                     </div>
                  </div>
               </div>
            </div>

            {/* New Table: Marketing Expense based on Dictated Amount */}
             <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
               <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-sm">
                 Marketing Expense based on Dictated Amount
               </div>
              <div className="w-full text-sm">
                <div className="text-center py-2 flex items-center justify-center">
                  <span>Collection <span className="italic">(net of 6% WHT and 1% Retention, if any)</span></span>
                </div>
                
                {/* Marketing Expense Row */}
                <div className="flex items-center text-right mb-1">
                  <div className="w-1/2 font-bold text-left pr-2 text-xs">Marketing Expense</div>
                  <div className="w-1/2 px-1 flex justify-end gap-1">
                    <span className="font-bold text-xs">₱</span>
                    <span>{formatCurrency(parseNumber(marketingExpense.total_me_amount))}</span>
                  </div>
                </div>

                {/* less Row */}
                <div className="flex items-center text-right mb-1">
                  <div className="w-1/2 font-bold text-left pr-2 text-xs italic">less</div>
                  <div className="w-1/2 px-1 flex justify-end gap-1">
                  </div>
                </div>

                {/* 12% VAT Row */}
                <div className="flex items-center text-right mb-1">
                  <div className="w-1/2 font-bold text-left pl-4 pr-2 text-xs flex justify-between items-center">
                     <span>12% VAT</span>
                     <label className="relative inline-flex items-center cursor-pointer mr-2">
                       <input 
                         type="checkbox" 
                         className="sr-only peer" 
                         checked={isMarketingVatEnabled}
                         onChange={() => setIsMarketingVatEnabled(!isMarketingVatEnabled)}
                       />
                       <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                     </label>
                  </div>
                  <div className="w-1/2 px-1 flex justify-end gap-1">
                    <span className="font-bold text-xs">₱</span>
                    <span>{formatCurrency(isMarketingVatEnabled ? parseNumber(marketingExpense.total_me_amount) * 0.12 : 0)}</span>
                  </div>
                </div>

                {/* Income Tax Row */}
                <div className="flex items-center text-right mb-1">
                  <div className="w-1/2 font-bold text-left pl-4 pr-2 text-xs flex justify-between items-center">
                     <span>Income Tax</span>
                     <label className="relative inline-flex items-center cursor-pointer mr-2">
                       <input 
                         type="checkbox" 
                         className="sr-only peer" 
                         checked={isMarketingIncomeTaxEnabled}
                         onChange={() => setIsMarketingIncomeTaxEnabled(!isMarketingIncomeTaxEnabled)}
                       />
                       <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                     </label>
                  </div>
                  <div className="w-1/2 px-1 flex justify-end gap-1 border-b border-black pb-1">
                    <span className="font-bold text-xs">₱</span>
                    <span>{formatCurrency(isMarketingIncomeTaxEnabled ? parseNumber(marketingExpense.total_me_amount) * 0.25 : 0)}</span>
                  </div>
                </div>

                {/* Empty Row */}
                <div className="h-4 w-full"></div>

                {/* TOTAL NET OF TAX Row */}
                <div className="flex items-center text-right mb-2">
                  <div className="w-1/2 font-bold text-left pr-2 text-xs text-red-600">TOTAL NET OF TAX</div>
                  <div className="w-1/2 px-1 flex justify-end gap-1 font-bold border-b-4 border-double border-black pb-1">
                    <span className="font-bold text-xs">₱</span>
                    <span>{formatCurrency(
                      parseNumber(marketingExpense.total_me_amount) - 
                      (isMarketingVatEnabled ? parseNumber(marketingExpense.total_me_amount) * 0.12 : 0) - 
                      (isMarketingIncomeTaxEnabled ? parseNumber(marketingExpense.total_me_amount) * 0.25 : 0)
                    )}</span>
                  </div>
                </div>
               </div>
             </div>

             {/* PROJECT COST COMPUTATION Table */}
            <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800 mt-6">
               <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-lg">PROJECT COST COMPUTATION</div>
               
               <div className="space-y-1 pl-4 text-sm">
                  <div className="flex justify-between items-center mb-1">
                     <span>Gross Profit</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompGrossProfit)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>Total OP</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompTotalOp)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1 font-bold">
                     <span>Gross Profit After OP</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent font-bold"
                            value={formatCurrency(newCompGrossProfitAfterOp)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>Rebates</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompRebates)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>Expenses</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompExpenses)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1 font-bold">
                     <span>Net Profit Before Tax</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompNetProfitBeforeTax)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <div className="flex items-center justify-between flex-1 pr-2">
                        <span>VAT</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                           <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={isNetProfitVatEnabled}
                              onChange={() => setIsNetProfitVatEnabled(!isNetProfitVatEnabled)}
                           />
                           <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                     </div>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(taxesPayableVatForNetProfit)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <div className="flex items-center justify-between flex-1 pr-2">
                        <span>Income Tax</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                           <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={isNetProfitIncomeTaxEnabled}
                              onChange={() => setIsNetProfitIncomeTaxEnabled(!isNetProfitIncomeTaxEnabled)}
                           />
                           <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                     </div>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(taxesPayableIncomeTaxForNetProfit)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1 font-bold">
                     <span>Net Profit After Tax</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompNetProfitAfterTax)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="text-center font-bold mb-2 mt-4 w-full">
                     LESS: TAX ON REBATES
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>VAT</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(taxesExposureVat)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>Income Tax</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(taxesExposureIncomeTax)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1 font-bold">
                     <span>Net Profit After Tax on Rebates</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={formatCurrency(newCompNetProfitAfterTaxOnRebatesFinal)}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="text-center font-bold mb-2 mt-4 w-full text-lg">
                     {compNetProfitAfterTaxOnRebatesPercentage.toFixed(2)}%
                  </div>
               </div>
            </div>
            
            {/* Signatures moved to bottom */}

          </div>
        </div>
        {/* Signatures */}
          <div className="pt-8 grid grid-cols-5 gap-4 items-end">
             {/* 1. Technical Specs */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Technical Specs Reviewed by:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                {isApproved ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/donald_madino.png" alt="Donald Madino Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedAt ? new Date(approvedAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : null}
                 <p className="font-bold uppercase text-sm">DONALD G. MADINO</p>
                 <p className="text-xs text-gray-600">Product Manager</p>
              </div>
           </div>

           {/* 2. Pricing Data */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Cost & Pricing Data Reviewed by:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedPricing ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/carol_dela_fuente.png" alt="Carol Dela Fuente Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedPricingAt ? new Date(approvedPricingAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedPricingAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : (currentUser?.email === 'cadf@isourceasiaworkplace.com') && (
                    <div className="mb-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAcceptPricing}
                       >
                          Accept
                       </Button>
                    </div>
                 )}
                 <p className="font-bold uppercase text-sm">Carol Dela Fuente</p>
                 <p className="text-xs text-gray-600">Supply Chain Manager</p>
              </div>
           </div>

           {/* 3. Costs & Price Accuracy */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">PCC Verified by:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedCosts || isDisapprovedCosts ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/odie_mejarito.png" alt="Odie Mejarito Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {(isApprovedCosts ? approvedCostsAt : disapprovedCostsAt) ? new Date(isApprovedCosts ? approvedCostsAt : disapprovedCostsAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(isApprovedCosts ? approvedCostsAt : disapprovedCostsAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : null}

                 {(currentUser?.email === 'oam@isourceasiaworkplace.com') && (
                    <div className="mb-1 flex flex-col items-center gap-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAcceptCosts}
                       >
                          Accept
                       </Button>
                       <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 text-xs"
                          onClick={handleDisapproveCosts}
                       >
                          Disapproved
                       </Button>
                    </div>
                 )}
                 {(isJcpUser || isMspUser) ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          role="button"
                          tabIndex={0}
                          className="font-bold uppercase text-sm cursor-pointer"
                          onClick={openAdjustmentNoteModal}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openAdjustmentNoteModal();
                            }
                          }}
                        >
                          Odie A. Mejarito
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Make a Note</TooltipContent>
                    </Tooltip>
                 ) : (
                    <p className="font-bold uppercase text-sm">Odie A. Mejarito</p>
                 )}
                 <p className="text-xs text-gray-600">Director for Operations</p>
              </div>
           </div>

           {/* 4. Additional Approver (Joygie) */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Reviewed for Approval:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedJoygie ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/joygie_pascual.png" alt="Joygie Pascual Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedJoygieAt ? new Date(approvedJoygieAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedJoygieAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : (currentUser?.email === 'jcp@isourceasia.com') && (
                    <div className="mb-1">
                       <Button
                          asChild
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                       >
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={handleAcceptJoygie}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleAcceptJoygie();
                              }
                            }}
                          >
                            Accept
                          </div>
                       </Button>
                    </div>
                 )}
                 {(isOamUser && (adjustmentNoteJcp || '').trim()) ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          role="button"
                          tabIndex={0}
                          className="font-bold uppercase text-sm cursor-pointer text-green-600"
                          onClick={() => openAdjustmentNoteViewer('jcp')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openAdjustmentNoteViewer('jcp');
                            }
                          }}
                        >
                          Joygie C. Pascual
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>View Note</TooltipContent>
                    </Tooltip>
                 ) : (
                    <p className={`font-bold uppercase text-sm ${isOamUser && (adjustmentNoteJcp || '').trim() ? 'text-green-600' : ''}`}>Joygie C. Pascual</p>
                 )}
                 <p className="text-xs text-gray-600">President</p>
              </div>
           </div>

           {/* 5. Overall Approver */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Overall Approver:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedOverall ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/margie_pascual.png" alt="Margie Pascual Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedOverallAt ? new Date(approvedOverallAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedOverallAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : (currentUser?.email === 'msp@isourceasia.com.ph') && (
                    <div className="mb-1">
                       <Button
                          asChild
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                       >
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={handleAcceptOverall}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleAcceptOverall();
                              }
                            }}
                          >
                            Accept
                          </div>
                       </Button>
                    </div>
                 )}
                 {(isOamUser && (adjustmentNoteMsp || '').trim()) ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          role="button"
                          tabIndex={0}
                          className="font-bold uppercase text-sm cursor-pointer text-green-600"
                          onClick={() => openAdjustmentNoteViewer('msp')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openAdjustmentNoteViewer('msp');
                            }
                          }}
                        >
                          Margie S. Pascual
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>View Note</TooltipContent>
                    </Tooltip>
                 ) : (
                    <p className={`font-bold uppercase text-sm ${isOamUser && (adjustmentNoteMsp || '').trim() ? 'text-green-600' : ''}`}>Margie S. Pascual</p>
                 )}
                 <p className="text-xs text-gray-600">CEO</p>
                </div>
            </div>
          </div>
         </div> {/* End of pdf-page-2-container */}
       </fieldset>

      <div className="flex justify-end mt-4 gap-2">
        {canExportPdf && (
          <Button 
            variant="default" 
            className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-6"
            onClick={handleExportPDF}
          >
            Export to PDF
          </Button>
        )}
        {!isReadOnly && (
          <Button 
            size="sm" 
            className="bg-green-600 hover:bg-green-700 text-white h-8 px-6"
            onClick={() => handleSaveAndBack()}
          >
            Save Changes
          </Button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={adjustmentNoteModalOpen} onOpenChange={setAdjustmentNoteModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Note for Adjustments</DialogTitle>
            <DialogDescription>
              {adjustmentNoteModalMode === 'view' ? 'View note below.' : 'Enter your note below (max 1000 characters).'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Textarea
              value={adjustmentNoteDraft}
              onChange={(e) => setAdjustmentNoteDraft(e.target.value.slice(0, 1000))}
              maxLength={1000}
              className="min-h-[220px]"
              readOnly={adjustmentNoteModalMode === 'view'}
            />
            <div className="text-xs text-gray-500 text-right">
              {(adjustmentNoteDraft || '').length}/1000
            </div>
          </div>
          <DialogFooter>
            {adjustmentNoteModalMode !== 'view' && (
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleSaveAdjustmentNote}
                disabled={!(isJcpUser || isMspUser)}
              >
                Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmationOpen} onOpenChange={setDeleteConfirmationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Row</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this row? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmationOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemoveItem}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retention Rate Modal */}
      <Dialog open={retentionModalOpen} onOpenChange={setRetentionModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Retention Rate</DialogTitle>
            <DialogDescription>
              Enter the new retention rate percentage below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="retention" className="text-right text-sm font-medium">
                Rate (%)
              </label>
              <Input
                id="retention"
                type="number"
                value={tempRetentionRate}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow numbers and decimals
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setTempRetentionRate(val);
                  }
                }}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetentionModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                const num = parseFloat(tempRetentionRate);
                if (!isNaN(num)) {
                  handleMarketingExpenseChange('retention_rate', num);
                  setRetentionModalOpen(false);
                } else if (tempRetentionRate === '') {
                  handleMarketingExpenseChange('retention_rate', 0);
                  setRetentionModalOpen(false);
                }
              }}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Search Modal */}
      <Dialog open={isInventoryModalOpen} onOpenChange={setIsInventoryModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Item</DialogTitle>
            <DialogDescription>
              Search for an item code to add to the project.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 overflow-hidden flex-1 mt-4">
            <Input 
              placeholder="Search by Item Code..."
              value={inventorySearchQuery}
              onChange={(e) => setInventorySearchQuery(e.target.value)}
              autoFocus
            />
            <div className="overflow-auto border rounded-md flex-1">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100 dark:bg-slate-800 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 font-medium">Brand</th>
                    <th className="p-3 font-medium">Model</th>
                    <th className="p-3 font-medium">Item Description</th>
                    <th className="p-3 font-medium">Item Code</th>
                    <th className="p-3 w-20 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {isFetchingInventory ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">Loading inventory...</td>
                    </tr>
                  ) : filteredInventoryItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">No items found.</td>
                    </tr>
                  ) : (
                    filteredInventoryItems.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                        <td className="p-3">{item.brand || '-'}</td>
                        <td className="p-3">{item.model || '-'}</td>
                        <td className="p-3">{item.itemDescription || item.description || '-'}</td>
                        <td className="p-3 font-medium">{item.itemCode || item.partNumber || '-'}</td>
                        <td className="p-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSelectInventoryItem(item)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsInventoryModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </TooltipProvider>
  );
};
